import { UIMetadataComplete } from '../../../types/UIMetadata';

/**
 * Chevrolet Tahoe (linkId 2365).
 *
 * UI half of the 2.0 metadata pair — every key mirrors a configurationId in
 * ./graphics-metadata.ts. Converted from the 1.0 repo's
 * `src/metadata/data/metadata-2365.ts`; shared UI boilerplate
 * (staticTranslations / saveFormConfig) follows the 2378 project.
 *
 * Conversion notes:
 *  - 1.0 `removeAccessories` + `defaultAccessory` become a shared graphics
 *    `groupId` + `isDefault` (2.0's single-select mechanism). groupIds are
 *    namespaced per trim because the SDK resolves them model-wide.
 *  - 1.0 `forColor` (allow-list of colour indices) is inverted into 2.0's
 *    `notAvailableForColor` deny-list of colour ids.
 *  - 1.0 `interactionApplyMessage` / `applyInteractions` become
 *    `affectedInteractionOnApply`; `includedPackages` become
 *    `dependencyPackages`.
 *  - Dropped, with no 2.0 equivalent: the `video` Welcome Animation
 *    interaction, feature `tempSkuToLoad`, interaction `meshAnimation` and
 *    `glowMesh`.
 *
 * Assets live in public/assets/{images,videos}/2365/ — paths below are relative
 * to `assets/` and resolved by `assetSrc()` in src/utils/metadata.ts.
 */
// Cast through unknown to allow additional model-level UI fields (e.g., name, specifications)
export const uimetadata = {
  models: {
    Tahoe: {
      variants: {
        'premier-4wd-2025': {
          configurationId: 'u5qm0qtkgk60',
          name: {
            en: '2025 Premier',
            ar: '2025 Premier'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'Premier',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/premier.png',
          price: {
            KSA: 318037,
            UAE: 312100,
            OMR: 33076,
            BHD: 32228,
            QAR: 308708,
            JOD: 60215,
            KW: 26291,
            IQD: 84810,
            LBP: 84810,
            default: 312100,
          },
          basePrice: {
            UAE: 312100,
            KSA: 318753,
            KW: 26043,
            BHD: 32049,
            OMR: 31240,
            QAR: 295400,
            JOD: 94700,
            IQD: 111355032,
            LBP: 7609189739,
            default: 312100
          },
          index: 0,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LZ',
          brandItemCode: 'CHTA08',
        },
        'premier-2wd-2025': {
          configurationId: '5vlpab0glq630',
          name: {
            en: '2025 Premier',
            ar: '2025 Premier'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'Premier',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/premier.png',
          price: {
            KSA: 309783,
            UAE: 304000,
            OMR: 32217,
            BHD: 31391,
            QAR: 300696,
            JOD: 58652,
            KW: 25609,
            IQD: 82609,
            LBP: 82609,
            default: 304000,
          },
          basePrice: {
            UAE: 304000,
            KSA: 310480,
            KW: 25367,
            BHD: 31217,
            JOD: 94700,
            OMR: 31240,
            QAR: 295400,
            IQD: 108465011,
            LBP: 7411706762,
            default: 304000
          },
          index: 1,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LZ',
          brandItemCode: 'CHTA07',
        },
        'z71-4wd-2025': {
          configurationId: '5vlpab0glq631',
          name: {
            en: '2025 Z71',
            ar: '2025 Z71'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'Z71',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/z71.png',
          price: {
            KSA: 282982,
            UAE: 277700,
            OMR: 29430,
            BHD: 28676,
            QAR: 274682,
            JOD: 53578,
            KW: 23393,
            IQD: 75462,
            LBP: 75462,
            default: 277700,
          },
          basePrice: {
            UAE: 277700,
            KSA: 283620,
            KW: 23172,
            BHD: 28516,
            JOD: 88000,
            OMR: 28390,
            QAR: 268400,
            IQD: 99081360,
            LBP: 6770496605,
            default: 277700
          },
          index: 2,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2Z7',
          brandItemCode: 'CHTA06',
        },
        'rst-4wd-2025': {
          configurationId: '5vlpab0glq632',
          name: {
            en: '2025 RST',
            ar: '2025 RST'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'RST',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/rst.png',
          price: {
            KSA: 293071,
            UAE: 287600,
            OMR: 30479,
            BHD: 29698,
            QAR: 284474,
            JOD: 55488,
            KW: 24227,
            IQD: 78152,
            LBP: 78152,
            default: 287600,
          },
          basePrice: {
            UAE: 287600,
            KSA: 293731,
            KW: 23998,
            BHD: 29533,
            JOD: 91100,
            OMR: 29330,
            QAR: 277300,
            IQD: 102613609,
            LBP: 7011864687,
            default: 287600
          },
          index: 3,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHTA05',
        },
        'lt-4wd-2025': {
          configurationId: 'baqct927elfe',
          name: {
            en: '2025 LT',
            ar: '2025 LT'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/lt.png',
          price: {
            KSA: 247724,
            UAE: 243100,
            OMR: 25763,
            BHD: 25103,
            QAR: 240458,
            JOD: 46902,
            KW: 20479,
            IQD: 66060,
            LBP: 66060,
            default: 243100,
          },
          basePrice: {
            UAE: 243100,
            KSA: 248282,
            KW: 20300,
            BHD: 24963,
            JOD: 72100,
            OMR: 23500,
            QAR: 222200,
            IQD: 86736329,
            LBP: 5926927348,
            default: 243100
          },
          index: 4,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHTA04',
        },
        'lt-2wd-2025': {
          configurationId: '5vlpab0glq633',
          name: {
            en: '2025 LT',
            ar: '2025 LT'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'LT',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/lt.png',
          price: {
            KSA: 239572,
            UAE: 235100,
            OMR: 24915,
            BHD: 24277,
            QAR: 232545,
            JOD: 45359,
            KW: 19805,
            IQD: 63886,
            LBP: 63886,
            default: 235100,
          },
          basePrice: {
            UAE: 235100,
            KSA: 240287,
            KW: 19632,
            BHD: 24142,
            JOD: 72100,
            OMR: 23500,
            QAR: 222200,
            IQD: 83881987,
            LBP: 5731882434,
            default: 235100
          },
          index: 5,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHTA03',
        },
        'ls-4wd-2025': {
          configurationId: 'ee6xolop3f2f',
          name: {
            en: '2025 LS',
            ar: '2025 LS'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LS',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/ls.png',
          price: {
            KSA: 239164,
            UAE: 234700,
            OMR: 24873,
            BHD: 24235,
            QAR: 232149,
            JOD: 45282,
            KW: 19771,
            IQD: 63777,
            LBP: 63777,
            default: 234700,
          },
          basePrice: {
            UAE: 234700,
            KSA: 239878,
            KW: 19598,
            BHD: 24101,
            JOD: 70800,
            OMR: 23080,
            QAR: 218200,
            IQD: 83739270,
            LBP: 5722130188,
            default: 234700
          },
          index: 6,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LS',
          brandItemCode: 'CHTA02',
        },
        'ls-2wd-2025': {
          configurationId: '5vlpab0glq635',
          name: {
            en: '2025 LS',
            ar: '2025 LS'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'LS',
          modelyear: '2025',
          image: 'images/2365/ext_colors/catalog/ls.png',
          price: {
            KSA: 230910,
            UAE: 226600,
            OMR: 24015,
            BHD: 23399,
            QAR: 224137,
            JOD: 43719,
            KW: 19089,
            IQD: 61576,
            LBP: 61576,
            default: 226600,
          },
          basePrice: {
            UAE: 226600,
            KSA: 231600,
            KW: 18922,
            BHD: 23269,
            JOD: 70800,
            OMR: 23080,
            QAR: 218200,
            IQD: 80849248,
            LBP: 5524647212,
            default: 226600
          },
          index: 7,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LS',
          brandItemCode: 'CHTA01',
        },
        'premier-4wd-2026': {
          configurationId: 'u5qml0qtkgk60',
          name: {
            en: '2026 Premier',
            ar: '2026 Premier'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'Premier',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/premier.png',
          price: {
            KSA: 318037,
            UAE: 312100,
            OMR: 33076,
            BHD: 32228,
            QAR: 308708,
            JOD: 60215,
            KW: 26291,
            IQD: 84810,
            LBP: 84810,
            default: 312100,
          },
          basePrice: {
            UAE: 312100,
            KSA: 318753,
            KW: 26043,
            QAR: 295400,
            BHD: 32049,
            JOD: 94700,
            OMR: 31240,
            IQD: 111355032,
            LBP: 7609189739,
            default: 312100
          },
          index: 8,
          isDefault: true,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LZ',
          brandItemCode: 'CHTA08',
        },
        'premier-2wd-2026': {
          configurationId: '5vlpab0ppglq630',
          name: {
            en: '2026 Premier',
            ar: '2026 Premier'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'Premier',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/premier.png',
          price: {
            KSA: 309783,
            UAE: 304000,
            OMR: 32217,
            BHD: 31391,
            QAR: 300696,
            JOD: 58652,
            KW: 25609,
            IQD: 82609,
            LBP: 82609,
            default: 304000,
          },
          basePrice: {
            UAE: 304000,
            KSA: 310480,
            KW: 25367,
            QAR: 295400,
            JOD: 94700,
            BHD: 31217,
            OMR: 31240,
            IQD: 108465011,
            LBP: 7411706762,
            default: 304000
          },
          index: 9,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LZ',
          brandItemCode: 'CHTA07',
        },
        'z71-4wd-2026': {
          configurationId: '55vlpab0glq631',
          name: {
            en: '2026 Z71',
            ar: '2026 Z71'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'Z71',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/z71.png',
          price: {
            KSA: 282982,
            UAE: 277700,
            OMR: 29430,
            BHD: 28676,
            QAR: 274682,
            JOD: 53578,
            KW: 23393,
            IQD: 75462,
            LBP: 75462,
            default: 277700,
          },
          basePrice: {
            UAE: 277700,
            KSA: 283620,
            KW: 23172,
            BHD: 28516,
            JOD: 88000,
            OMR: 28390,
            QAR: 268400,
            IQD: 99081360,
            LBP: 6770496605,
            default: 277700
          },
          index: 10,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '2Z7',
          brandItemCode: 'CHTA06',
        },
        'rst-4wd-2026': {
          configurationId: '5vlpahb0glq632',
          name: {
            en: '2026 RST',
            ar: '2026 RST'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'RST',
          modelyear: '2026',
          image: 'images/2365/variant_rst_2026.png',
          price: {
            KSA: 293071,
            UAE: 287600,
            OMR: 30479,
            BHD: 29698,
            QAR: 284474,
            JOD: 55488,
            KW: 24227,
            IQD: 78152,
            LBP: 78152,
            default: 287600,
          },
          basePrice: {
            UAE: 287600,
            KSA: 293731,
            KW: 23998,
            JOD: 91100,
            BHD: 29533,
            OMR: 29330,
            QAR: 277300,
            IQD: 102613609,
            LBP: 7011864687,
            default: 287600
          },
          index: 11,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1SP',
          brandItemCode: 'CHTA05',
        },
        'lt-4wd-2026': {
          configurationId: 'b09aqct927elfe',
          name: {
            en: '2026 LT',
            ar: '2026 LT'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LT',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/lt.png',
          price: {
            KSA: 247724,
            UAE: 243100,
            OMR: 25763,
            BHD: 25103,
            QAR: 240458,
            JOD: 46902,
            KW: 20479,
            IQD: 66060,
            LBP: 66060,
            default: 243100,
          },
          basePrice: {
            UAE: 243100,
            KSA: 248282,
            KW: 20300,
            QAR: 222200,
            JOD: 72100,
            OMR: 23500,
            BHD: 24963,
            IQD: 86736329,
            LBP: 5926927348,
            default: 243100
          },
          index: 12,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHTA04',
        },
        'lt-2wd-2026': {
          configurationId: '5vlpabpo0glq633',
          name: {
            en: '2026 LT',
            ar: '2026 LT'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'LT',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/lt.png',
          price: {
            KSA: 239572,
            UAE: 235100,
            OMR: 24915,
            BHD: 24277,
            QAR: 232545,
            JOD: 45359,
            KW: 19805,
            IQD: 63886,
            LBP: 63886,
            default: 235100,
          },
          basePrice: {
            UAE: 235100,
            KSA: 240287,
            KW: 19632,
            JOD: 72100,
            BHD: 24142,
            OMR: 23500,
            QAR: 222200,
            IQD: 83881987,
            LBP: 5731882434,
            default: 235100
          },
          index: 13,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LT',
          brandItemCode: 'CHTA03',
        },
        'ls-4wd-2026': {
          configurationId: 'ee6xollpop3f2f',
          name: {
            en: '2026 LS',
            ar: '2026 LS'
          },
          description: {
            en: '4WD',
            ar: 'الدفع الرباعي'
          },
          drivetrain: '4WD',
          trim: 'LS',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/ls.png',
          price: {
            KSA: 239164,
            UAE: 234700,
            OMR: 24873,
            BHD: 24235,
            QAR: 232149,
            JOD: 45282,
            KW: 19771,
            IQD: 63777,
            LBP: 63777,
            default: 234700,
          },
          basePrice: {
            UAE: 234700,
            KSA: 239878,
            KW: 19598,
            BHD: 24101,
            JOD: 70800,
            OMR: 23080,
            QAR: 218200,
            IQD: 83739270,
            LBP: 5722130188,
            default: 234700
          },
          index: 14,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LS',
          brandItemCode: 'CHTA02',
        },
        'ls-2wd-2026': {
          configurationId: '5vlggpab0glq635',
          name: {
            en: '2026 LS',
            ar: '2026 LS'
          },
          description: {
            en: '2WD',
            ar: 'الدفع بعجلتين'
          },
          drivetrain: '2WD',
          trim: 'LS',
          modelyear: '2026',
          image: 'images/2365/ext_colors/catalog/ls.png',
          price: {
            KSA: 230910,
            UAE: 226600,
            OMR: 24015,
            BHD: 23399,
            QAR: 224137,
            JOD: 43719,
            KW: 19089,
            IQD: 61576,
            LBP: 61576,
            default: 226600,
          },
          basePrice: {
            UAE: 226600,
            KSA: 231600,
            KW: 18922,
            OMR: 23080,
            JOD: 70800,
            BHD: 23269,
            QAR: 218200,
            IQD: 80849248,
            LBP: 5524647212,
            default: 226600
          },
          index: 15,
          isDefault: false,
          ar: {
            enable: false,
            redirection: false
          },
          highlights: [],
          specs: [],
          toggleTabs: {
            CONFIG: {
              mobile: true,
              desktop: true
            },
            MODEL: {
              mobile: true,
              desktop: true
            },
            EXTERIOR: {
              mobile: true,
              desktop: true
            },
            INTERIOR: {
              mobile: true,
              desktop: true
            },
            OPTIONS: {
              mobile: true,
              desktop: true
            },
            SUMMARY: {
              mobile: true,
              desktop: true
            }
          },
          autoIntelRPOCode: '1LS',
          brandItemCode: 'CHTA01',
        }
      },
      backgrounds: {
        g0ZA968_RpUd: {
          image: 'images/2365/change_bg_3.webp',
          title: {
            en: 'City Landscape 1',
            ar: 'غروب الشمس على الشاطئ'
          }
        },
        g0ZA968_RpUe: {
          image: 'images/2365/HDRI/hdri_2.png',
          title: {
            en: 'Sand Dunes',
            ar: 'الكثبان الرملية'
          }
        }
      },
      ambientLighting: {},
      colors: {
        interior: {
          bmbnnt678r0o: {
            label: {
              en: 'Jet Black/ Sky Cool Gray',
              ar: 'أسود نفاث/رمادي سماوي بارد'
            },
            image: 'images/2365/Interiors/official/Jet-Black-Sky-Cool-Gray-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HUN',
            brandItemCode: 'IN04',
          },
          vltii7siyl2j: {
            label: {
              en: 'Jet Black/ Sky Cool Gray',
              ar: 'أسود نفاث/رمادي سماوي بارد'
            },
            image: 'images/2365/Interiors/official/Jet-Black-Sky-Cool-Gray-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HUN',
            brandItemCode: 'IN04',
          },
          uyr964nuipvo: {
            label: {
              en: 'Gideon/ Very Dark Atmosphere Leather',
              ar: 'جدعون / بشرة داكنة جدًا'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVC',
            brandItemCode: 'IN25',
          },
          dlhw2j4wp06f: {
            label: {
              en: 'Jet Black/ Victory Red Leather',
              ar: 'جلد أسود نفاث/أحمر النصر'
            },
            image: 'images/2365/Interiors/official/Jet-Black-Victory-Red-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVA',
            brandItemCode: 'IN26',
          },
          '4s1b4jrlyp72': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'جدعون / قماش جو مظلم للغاية'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN27',
          },
          '4fwo0ktql1c6': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جدعون / بشرة داكنة جدًا'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 3900,
              UAE: 3827,
              OMR: 406,
              BHD: 395,
              QAR: 3785,
              JOD: 738,
              KW: 322,
              IQD: 1040,
              LBP: 1040,
              default: 3827,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Interior Color',
                  ar: 'اللون الداخلي'
                },
                label: {
                  en: 'Gideon / Very Dark Atmosphere Leather',
                  ar: 'جدعون / بشرة داكنة جدًا'
                },
                price: {
                  KSA: 3900,
                  UAE: 3827,
                  OMR: 406,
                  BHD: 395,
                  QAR: 3785,
                  JOD: 738,
                  KW: 322,
                  IQD: 1040,
                  LBP: 1040,
                  default: 3827,
                }
              }
            ],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVC',
            brandItemCode: 'IN25',
          },
          br6qka4lhl4f: {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'جدعون / قماش جو مظلم للغاية'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN27',
          },
          oeiqe7hal1j3: {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جدعون / بشرة داكنة جدًا'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 3900,
              UAE: 3827,
              OMR: 406,
              BHD: 395,
              QAR: 3785,
              JOD: 738,
              KW: 322,
              IQD: 1040,
              LBP: 1040,
              default: 3827,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Interior Color',
                  ar: 'اللون الداخلي'
                },
                label: {
                  en: 'Gideon / Very Dark Atmosphere Leather',
                  ar: 'جدعون / بشرة داكنة جدًا'
                },
                price: {
                  KSA: 3900,
                  UAE: 3827,
                  OMR: 406,
                  BHD: 395,
                  QAR: 3785,
                  JOD: 738,
                  KW: 322,
                  IQD: 1040,
                  LBP: 1040,
                  default: 3827,
                }
              }
            ],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVC',
            brandItemCode: 'IN25',
          },
          '9zod8og9izu9': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'جدعون / قماش جو مظلم للغاية'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN27',
          },
          nfs4tw1z0y5q: {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جدعون / بشرة داكنة جدًا'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 3900,
              UAE: 3827,
              OMR: 406,
              BHD: 395,
              QAR: 3785,
              JOD: 738,
              KW: 322,
              IQD: 1040,
              LBP: 1040,
              default: 3827,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Interior Color',
                  ar: 'اللون الداخلي'
                },
                label: {
                  en: 'Gideon / Very Dark Atmosphere Leather',
                  ar: 'جدعون / بشرة داكنة جدًا'
                },
                price: {
                  KSA: 3900,
                  UAE: 3827,
                  OMR: 406,
                  BHD: 395,
                  QAR: 3785,
                  JOD: 738,
                  KW: 322,
                  IQD: 1040,
                  LBP: 1040,
                  default: 3827,
                }
              }
            ],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVC',
            brandItemCode: 'IN25',
          },
          '48za413x8262': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Cloth',
              ar: 'جدعون / قماش جو مظلم للغاية'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Cloth.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            },
            priceBreakdown: [],
            index: 0,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HV5',
            brandItemCode: 'IN27',
          },
          '48za413x82623': {
            label: {
              en: 'Gideon / Very Dark Atmosphere Leather',
              ar: 'جدعون / بشرة داكنة جدًا'
            },
            image: 'images/2365/Interiors/official/Gideon-Very-Dark-Atmosphere-Leather.jpg',
            usdz: 'interior_red',
            price: {
              KSA: 3900,
              UAE: 3827,
              OMR: 406,
              BHD: 395,
              QAR: 3785,
              JOD: 738,
              KW: 322,
              IQD: 1040,
              LBP: 1040,
              default: 3827,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Interior Color',
                  ar: 'اللون الداخلي'
                },
                label: {
                  en: 'Gideon / Very Dark Atmosphere Leather',
                  ar: 'جدعون / بشرة داكنة جدًا'
                },
                price: {
                  KSA: 3900,
                  UAE: 3827,
                  OMR: 406,
                  BHD: 395,
                  QAR: 3785,
                  JOD: 738,
                  KW: 322,
                  IQD: 1040,
                  LBP: 1040,
                  default: 3827,
                }
              }
            ],
            index: 1,
            type: 'interior',
            cardType: 'cart',
            cartImage: '',
            hasQuantity: false,
            autoIntelRPOCode: 'HVC',
            brandItemCode: 'IN25',
          }
        },
        exterior: {
          k550ufl2z63j: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          gxqxsxnaljfj: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          '9i25yo15msgi': {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          l415icrduyfe: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          t0ah6d0ze4xe: {
            label: {
              en: 'Iridescent Pearl Tintcoat',
              ar: 'لؤلؤي ثلاثي الطبقات'
            },
            image: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Iridescent Pearl Tintcoat',
                  ar: 'لؤلؤي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX35',
          },
          't0ah6d0ze4xe-my26': {
            label: {
              en: 'Polar White Tricoat',
              ar: 'القطبية البيضاء ثلاثية المعطف'
            },
            image: 'images/2365/ext_colors/official/Polar-White-Tricoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'القطبية البيضاء ثلاثية المعطف'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Polar-White-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX29',
          },
          ew6nnknehod1: {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          c5rilpej5j26: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          o3nu3afykesw: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          fs9rdujntrrb: {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          d5ct2pjih82f: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          mi7h9vt1eqo1: {
            label: {
              en: 'Iridescent Pearl Tintcoat',
              ar: 'لؤلؤي ثلاثي الطبقات'
            },
            image: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Iridescent Pearl Tintcoat',
                  ar: 'لؤلؤي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX35',
          },
          'mi7h9vt1eqo1-my26': {
            label: {
              en: 'Polar White Tricoat',
              ar: 'القطبية البيضاء ثلاثية المعطف'
            },
            image: 'images/2365/ext_colors/official/Polar-White-Tricoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Polar White Tricoat',
                  ar: 'القطبية البيضاء ثلاثية المعطف'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Polar-White-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G4J',
            brandItemCode: 'EX29',
          },
          aznfg8hzvuk5: {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          qq29ff92khmy: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          clohkdql579f: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          tesd2qbp9csg: {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          no2ztgqx4h18: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          b6t97b02hemr: {
            label: {
              en: 'Cypress Grey',
              ar: 'السرو غراي'
            },
            image: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 1301,
              UAE: 1277,
              OMR: 135,
              BHD: 132,
              QAR: 1263,
              JOD: 246,
              KW: 108,
              IQD: 347,
              LBP: 347,
              default: 1277,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Grey',
                  ar: 'السرو غراي'
                },
                price: {
                  KSA: 1301,
                  UAE: 1277,
                  OMR: 135,
                  BHD: 132,
                  QAR: 1263,
                  JOD: 246,
                  KW: 108,
                  IQD: 347,
                  LBP: 347,
                  default: 1277,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX36',
          },
          'b6t97b02hemr-my26': {
            label: {
              en: 'Cypress Grey',
              ar: 'السرو غراي'
            },
            image: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 1301,
              UAE: 1277,
              OMR: 135,
              BHD: 132,
              QAR: 1263,
              JOD: 246,
              KW: 108,
              IQD: 347,
              LBP: 347,
              default: 1277,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Grey',
                  ar: 'السرو غراي'
                },
                price: {
                  KSA: 1301,
                  UAE: 1276.73,
                  OMR: 135,
                  BHD: 132,
                  QAR: 1263,
                  JOD: 246,
                  KW: 108,
                  LBP: 347,
                  IQD: 347,
                  default: 1276.73,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX36',
          },
          '5ydijea16amw': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          a38irmi8sgbh: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          fso5sn4r0cx8: {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          ry4r9fqb0aes: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          vt02zytt1nuj: {
            label: {
              en: 'Iridescent Pearl Tintcoat',
              ar: 'لؤلؤي ثلاثي الطبقات'
            },
            image: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Iridescent Pearl Tintcoat',
                  ar: 'لؤلؤي ثلاثي الطبقات'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Iridescent-Pearl-Tricoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G1W',
            brandItemCode: 'EX35',
          },
          'vt02zytt1nuj-my26': {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '9do24jiu9hik': {
            label: {
              en: 'Cypress Grey',
              ar: 'السرو غراي'
            },
            image: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            usdz: 'blue',
            price: {
              KSA: 1301,
              UAE: 1277,
              OMR: 135,
              BHD: 132,
              QAR: 1263,
              JOD: 246,
              KW: 108,
              IQD: 347,
              LBP: 347,
              default: 1277,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Cypress Grey',
                  ar: 'السرو غراي'
                },
                price: {
                  KSA: 1301,
                  UAE: 1277,
                  OMR: 135,
                  BHD: 132,
                  QAR: 1263,
                  JOD: 246,
                  KW: 108,
                  IQD: 347,
                  LBP: 347,
                  default: 1277,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Cypress-Gray.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBD',
            brandItemCode: 'EX36',
          },
          phik3xi1layc: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          dsv2burvpj4y: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          kx78eegb5tx6: {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          fvoiamkx0yvo: {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          '54x2digmt4d0': {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          g89wq89fizzz: {
            label: {
              en: 'Summit White',
              ar: 'أبيض'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'أبيض'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          r4pz2mma73zm: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          c1asri6sqbvs: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          '52q7wm3fkk2r': {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          '54sh9pp059zy': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          '74zty88mapb6': {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          '5ygv706j1y4i': {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          rjay25qlmny3: {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          xlqf75i2saml: {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          ab3bl6ua4odh: {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          '8h4v9q6ubtyq': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          gsuts8nmckv2: {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          oavzfo5fep46: {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          '37xytt0490j8': {
            label: {
              en: 'Radiant Red Tintcoat',
              ar: 'طلاء أحمر مشع'
            },
            image: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            usdz: 'red_black',
            price: {
              KSA: 1630,
              UAE: 1600,
              OMR: 170,
              BHD: 165,
              QAR: 1583,
              JOD: 309,
              KW: 135,
              IQD: 435,
              LBP: 435,
              default: 1600,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Radiant Red Tintcoat',
                  ar: 'طلاء أحمر مشع'
                },
                price: {
                  KSA: 1630,
                  UAE: 1600,
                  OMR: 170,
                  BHD: 165,
                  QAR: 1583,
                  JOD: 309,
                  KW: 135,
                  IQD: 435,
                  LBP: 435,
                  default: 1600,
                }
              }
            ],
            index: 0,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Radiant-Red-Tintcoat.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GNT',
            brandItemCode: 'EX03',
          },
          '5xzh26yrdinw': {
            label: {
              en: 'Black',
              ar: 'أسود'
            },
            image: 'images/2365/ext_colors/official/Black.jpg',
            usdz: 'red',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'طلاء أحمر مشع'
                },
                label: {
                  en: 'Black',
                  ar: 'أسود'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 1,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Black.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GBA',
            brandItemCode: 'EX02',
          },
          '2fa06p9t7v8f': {
            label: {
              en: 'Dark Ash Metallic',
              ar: 'رمادي داكن معدني'
            },
            image: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            usdz: 'black',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Dark Ash Metallic',
                  ar: 'رمادي داكن معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 2,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Dark-Ash-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'G6M',
            brandItemCode: 'EX11',
          },
          '10xy9i682nxc': {
            label: {
              en: 'Sterling Gray Metallic',
              ar: 'رمادي استرليني معدني'
            },
            image: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Sterling Gray Metallic',
                  ar: 'رمادي استرليني معدني'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 3,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Sterling-Gray-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXD',
            brandItemCode: 'EX05',
          },
          '10xy9i682nxc4': {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 556,
              UAE: 546,
              OMR: 58,
              BHD: 56,
              QAR: 540,
              JOD: 105,
              KW: 46,
              IQD: 148,
              LBP: 148,
              default: 546,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 556,
                  UAE: 546,
                  OMR: 58,
                  BHD: 56,
                  QAR: 540,
                  JOD: 105,
                  KW: 46,
                  IQD: 148,
                  LBP: 148,
                  default: 546,
                }
              }
            ],
            index: 4,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          },
          '10xy9i682nxc5': {
            label: {
              en: 'Lakeshore Blue Metallic',
              ar: 'أزرق ليكشور ميتاليك'
            },
            image: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            usdz: 'blue',
            price: {
              KSA: 2055,
              UAE: 2017,
              OMR: 214,
              BHD: 208,
              QAR: 1995,
              JOD: 389,
              KW: 170,
              IQD: 548,
              LBP: 548,
              default: 2017,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Lakeshore Blue Metallic',
                  ar: 'أزرق ليكشور ميتاليك'
                },
                price: {
                  KSA: 2055,
                  UAE: 2017,
                  OMR: 214,
                  BHD: 208,
                  QAR: 1995,
                  JOD: 389,
                  KW: 170,
                  IQD: 548,
                  LBP: 548,
                  default: 2017,
                }
              }
            ],
            index: 5,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Lakeshore-Blue-Metallic.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GXP',
            brandItemCode: 'EX14',
          },
          vt02ttzytt1nuj: {
            label: {
              en: 'Summit White',
              ar: 'القمة البيضاء'
            },
            image: 'images/2365/ext_colors/official/Summit-White.jpg',
            usdz: 'blue',
            price: {
              KSA: 3277,
              UAE: 3216,
              OMR: 341,
              BHD: 332,
              QAR: 3181,
              JOD: 620,
              KW: 271,
              IQD: 874,
              LBP: 874,
              default: 3216,
            },
            priceBreakdown: [
              {
                title: {
                  en: 'Exterior Color',
                  ar: 'اللون الخارجي'
                },
                label: {
                  en: 'Summit White',
                  ar: 'القمة البيضاء'
                },
                price: {
                  KSA: 3277,
                  UAE: 3216,
                  OMR: 341,
                  BHD: 332,
                  QAR: 3181,
                  JOD: 620,
                  KW: 271,
                  IQD: 874,
                  LBP: 874,
                  default: 3216,
                }
              }
            ],
            index: 6,
            type: 'exterior',
            cardType: 'cart',
            cartImage: 'images/2365/ext_colors/official/Summit-White.jpg',
            hasQuantity: false,
            autoIntelRPOCode: 'GAZ',
            brandItemCode: 'EX01',
          }
        }
      },
      features: {
        ylam62d7djz9: {
          label: {
            en: 'Redesigned Unique Grill Pattern',
            ar: 'نمط الشواية الفريد المعاد تصميمه'
          },
          image: 'images/2365/Premier/ChromeGrill.png',
          title: {
            en: 'Redesigned Unique Grill Pattern',
            ar: 'نمط الشواية الفريد المعاد تصميمه'
          },
          desc: {
            en: 'Redesigned Front Chrome Fascia',
            ar: 'واجهة أمامية من الكروم مُعاد تصميمها'
          },
          type: 'exterior'
        },
        kh1xk6tsachv: {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'Signature Front Lighting',
            ar: 'الإضاءة الأمامية المميزة'
          },
          type: 'exterior'
        },
        '1slkxz7syxvp': {
          label: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          desc: {
            en: 'New Rear Tail Lamps',
            ar: 'مصابيح خلفية جديدة'
          },
          type: 'exterior'
        },
        kbns5f4ez323: {
          label: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          image: 'images/2365//R4C.png',
          title: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          desc: {
            en: 'Striking 22" Chrome Wheels',
            ar: 'عجلات كروم مذهلة مقاس 22 بوصة'
          },
          type: 'exterior'
        },
        k1gebz2rhrfs: {
          label: {
            en: 'Chrome Side Mirrors',
            ar: 'مرايا جانبية كروم'
          },
          image: 'images/2365/Premier/ChromeSideMirrors.png',
          title: {
            en: 'Chrome Side Mirrors',
            ar: 'مرايا جانبية كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '511hi24a2yfb': {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        dk4jj9hgugds: {
          label: {
            en: 'Power Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية كهربائية'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          title: {
            en: 'Power Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية كهربائية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '1dozmck8mjut': {
          label: {
            en: 'LED Front Fog Lamps',
            ar: 'مصابيح الضباب الأمامية LED'
          },
          image: 'images/2365/Premier/LEDFrontFogLamps.png',
          title: {
            en: 'LED Front Fog Lamps',
            ar: 'مصابيح الضباب الأمامية LED'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        y2e6haahw9vd: {
          label: {
            en: 'Power Retractable Side step with Perimeter Lighting',
            ar: 'خطوة جانبية قابلة للسحب كهربائيًا مع إضاءة محيطية'
          },
          image: 'images/2365/Premier/AssistStep.png',
          title: {
            en: 'Power Retractable Side step with Perimeter Lighting',
            ar: 'خطوة جانبية قابلة للسحب كهربائيًا مع إضاءة محيطية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        y6zcqp9r8fln: {
          label: {
            en: 'All new Dashboard Design',
            ar: 'تصميم لوحة القيادة الجديد كليًا'
          },
          image: 'images/2365/Premier/SteeringWheelDesign.png',
          title: {
            en: 'All new Dashboard Design',
            ar: 'تصميم لوحة القيادة الجديد كليًا'
          },
          desc: {
            en: 'New Dashboard and Steering Wheel Design',
            ar: 'تصميم جديد للوحة القيادة وعجلة القيادة'
          },
          type: 'interior'
        },
        xsdac8nojc1s: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        ddlwz0n6pzn8: {
          label: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        '9cffzj69hili': {
          label: {
            en: 'Rear Dual Entertainment Screen',
            ar: 'شاشة ترفيه مزدوجة خلفية'
          },
          image: 'images/2365/Premier/RearDualEntertainmentScreen.png',
          title: {
            en: 'Rear Dual Entertainment Screen',
            ar: 'شاشة ترفيه مزدوجة خلفية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        e6jhw2a3jpjj: {
          label: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          image: 'images/2365/information_center.png',
          title: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          desc: {
            en: 'Advanced Electronic Column Shifter',
            ar: 'ناقل عمود إلكتروني متقدم'
          },
          type: 'interior'
        },
        hnbca1qrws9d: {
          label: {
            en: 'Premier Exclusive Jet Black and Sky Cool Grey Leather Interior',
            ar: 'مقصورة داخلية فاخرة من الجلد باللون الأسود النفاث والرمادي السماوي'
          },
          image: 'images/2365/Premier/Premierleather.png',
          title: {
            en: 'Premier Exclusive Jet Black and Sky Cool Grey Leather Interior',
            ar: 'مقصورة داخلية فاخرة من الجلد باللون الأسود النفاث والرمادي السماوي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '19t5w7j2faxq': {
          label: {
            en: 'Front Cooled and Heated Leather Seats',
            ar: 'مقاعد أمامية جلدية مُبردة ومُدفأة'
          },
          image: 'images/2365/Interiors/Premier_Interior.jpg',
          title: {
            en: 'Front Cooled and Heated Leather Seats',
            ar: 'مقاعد أمامية جلدية مُبردة ومُدفأة'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        na9oo3ba0gf9: {
          label: {
            en: 'Redesigned Unique Grill Pattern',
            ar: 'نمط الشواية الفريد المعاد تصميمه'
          },
          image: 'images/2365/Premier/ChromeGrill.png',
          title: {
            en: 'Redesigned Unique Grill Pattern',
            ar: 'نمط الشواية الفريد المعاد تصميمه'
          },
          desc: {
            en: 'Redesigned Front Chrome Fascia',
            ar: 'واجهة أمامية من الكروم مُعاد تصميمها'
          },
          type: 'exterior'
        },
        je1762muldx3: {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'Signature Front Lighting',
            ar: 'الإضاءة الأمامية المميزة'
          },
          type: 'exterior'
        },
        '0hw6i0eruidm': {
          label: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          desc: {
            en: 'New Rear Tail Lamps',
            ar: 'مصابيح خلفية جديدة'
          },
          type: 'exterior'
        },
        qwurip7ebhzl: {
          label: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          image: 'images/2365//R4C.png',
          title: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          desc: {
            en: 'Striking 22" Chrome Wheels',
            ar: 'عجلات كروم مذهلة مقاس 22 بوصة'
          },
          type: 'exterior'
        },
        c7g9x5k81ma6: {
          label: {
            en: 'Chrome Side Mirrors',
            ar: 'مرايا جانبية كروم'
          },
          image: 'images/2365/Premier/ChromeSideMirrors.png',
          title: {
            en: 'Chrome Side Mirrors',
            ar: 'مرايا جانبية كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        dvnbl0ekb85x: {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        shbi0bb2sa3u: {
          label: {
            en: 'Power Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية كهربائية'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          title: {
            en: 'Power Panaromic Sunroof',
            ar: 'فتحة سقف بانورامية كهربائية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        k4xomlfi21su: {
          label: {
            en: 'LED Front Fog Lamps',
            ar: 'مصابيح الضباب الأمامية LED'
          },
          image: 'images/2365/Premier/LEDFrontFogLamps.png',
          title: {
            en: 'LED Front Fog Lamps',
            ar: 'مصابيح الضباب الأمامية LED'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '02a9r2m2zso3': {
          label: {
            en: 'Power Retractable Side step with Perimeter Lighting',
            ar: 'خطوة جانبية قابلة للسحب كهربائيًا مع إضاءة محيطية'
          },
          image: 'images/2365/Premier/AssistStep.png',
          title: {
            en: 'Power Retractable Side step with Perimeter Lighting',
            ar: 'خطوة جانبية قابلة للسحب كهربائيًا مع إضاءة محيطية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        g9s8rytddijb: {
          label: {
            en: 'Suspension',
            ar: 'تعليق'
          },
          image: 'images/2365/Premier/Suspension.png',
          title: {
            en: 'Suspension',
            ar: 'تعليق'
          },
          desc: {
            en: 'Adaptive Suspension Providing Comfort and Control',
            ar: 'نظام التعليق التكيفي الذي يوفر الراحة والتحكم'
          },
          type: 'exterior'
        },
        nrol7dpp8yd0: {
          label: {
            en: 'All new Dashboard Design',
            ar: 'تصميم لوحة القيادة الجديد كليًا'
          },
          image: 'images/2365/Premier/SteeringWheelDesign.png',
          title: {
            en: 'All new Dashboard Design',
            ar: 'تصميم لوحة القيادة الجديد كليًا'
          },
          desc: {
            en: 'New Dashboard and Steering Wheel Design',
            ar: 'تصميم جديد للوحة القيادة وعجلة القيادة'
          },
          type: 'interior'
        },
        zimbl7yvpilx: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        jzk46gu6adwb: {
          label: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        bsqsmyygj2bg: {
          label: {
            en: 'Rear Dual Entertainment Screen',
            ar: 'شاشة ترفيه مزدوجة خلفية'
          },
          image: 'images/2365/Premier/RearDualEntertainmentScreen.png',
          title: {
            en: 'Rear Dual Entertainment Screen',
            ar: 'شاشة ترفيه مزدوجة خلفية'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        mae94ricnkww: {
          label: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          image: 'images/2365/information_center.png',
          title: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          desc: {
            en: 'Advanced Electronic Column Shifter',
            ar: 'ناقل عمود إلكتروني متقدم'
          },
          type: 'interior'
        },
        o6s36z8hkm4s: {
          label: {
            en: 'Premier Exclusive Jet Black and Sky Cool Grey Leather Interior',
            ar: 'مقصورة داخلية فاخرة من الجلد باللون الأسود النفاث والرمادي السماوي'
          },
          image: 'images/2365/Premier/Premierleather.png',
          title: {
            en: 'Premier Exclusive Jet Black and Sky Cool Grey Leather Interior',
            ar: 'مقصورة داخلية فاخرة من الجلد باللون الأسود النفاث والرمادي السماوي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '5glz7rcq53uo': {
          label: {
            en: 'Front Cooled and Heated Leather Seats',
            ar: 'مقاعد أمامية جلدية مُبردة ومُدفأة'
          },
          image: 'images/2365/Interiors/Premier_Interior.jpg',
          title: {
            en: 'Front Cooled and Heated Leather Seats',
            ar: 'مقاعد أمامية جلدية مُبردة ومُدفأة'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        kyfij9hxv5nt: {
          label: {
            en: 'Black Grille Design',
            ar: 'تصميم مصبغة سوداء'
          },
          image: 'images/2365//BlackGrilleDesign.png',
          title: {
            en: 'Black Grille Design',
            ar: 'تصميم مصبغة سوداء'
          },
          desc: {
            en: 'Bold Front Black Grille with Z71 Badging',
            ar: 'شبكة أمامية سوداء جريئة مع شعار Z71'
          },
          type: 'exterior'
        },
        '4wbktfrvcpql': {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'Redesigned LED Headlamps and Fog Lamps',
            ar: 'مصابيح أمامية LED ومصابيح ضباب مُعاد تصميمها'
          },
          type: 'exterior'
        },
        wrbhrpf0p4mu: {
          label: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          desc: {
            en: 'Redesigned LED Tail Lamps',
            ar: 'مصابيح خلفية LED مُعاد تصميمها'
          },
          type: 'exterior'
        },
        xfjqlf6ze3pm: {
          label: {
            en: '20" Wheels Z71 Specific',
            ar: 'عجلات مقاس 20 بوصة مخصصة لطراز Z71'
          },
          image: 'images/2365//R4M.png',
          title: {
            en: '20" Wheels Z71 Specific',
            ar: 'عجلات مقاس 20 بوصة مخصصة لطراز Z71'
          },
          desc: {
            en: '20" Z71 Specific Off-Road Wheels',
            ar: 'عجلات Z71 مخصصة للطرق الوعرة مقاس 20 بوصة'
          },
          type: 'exterior'
        },
        gbdtk5x81fz0: {
          label: {
            en: 'Red Horizontal Recovery Hooks',
            ar: 'خطافات الاسترداد الأفقية الحمراء'
          },
          image: 'images/2365/RedHorizontalRecoveryHooks1.png',
          title: {
            en: 'Red Horizontal Recovery Hooks',
            ar: 'خطافات الاسترداد الأفقية الحمراء'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '3pn95exro9z6': {
          label: {
            en: 'Tubular Black Side Step',
            ar: 'خطوة جانبية أنبوبية سوداء'
          },
          image: 'images/2365/TubularBlackSideStep.png',
          title: {
            en: 'Tubular Black Side Step',
            ar: 'خطوة جانبية أنبوبية سوداء'
          },
          desc: {
            en: 'Rugged Tubular Black Side Step',
            ar: 'خطوة جانبية أنبوبية سوداء متينة'
          },
          type: 'exterior'
        },
        '5axgfh2btytx': {
          label: {
            en: 'Front Row Bucket Seats',
            ar: 'مقاعد دلو الصف الأمامي'
          },
          image: 'images/2365/Interiors/Z71 Interior.jpg',
          title: {
            en: 'Front Row Bucket Seats',
            ar: 'مقاعد دلو الصف الأمامي'
          },
          desc: {
            en: 'Leather Bucket Seats',
            ar: 'مقاعد دلو جلدية'
          },
          type: 'interior'
        },
        bt48x44z13b4: {
          label: {
            en: 'Front Wireless Charger',
            ar: 'شاحن لاسلكي أمامي'
          },
          image: 'images/2365//FrontWirelessCharger.png',
          title: {
            en: 'Front Wireless Charger',
            ar: 'شاحن لاسلكي أمامي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        gbe4ixdirr8d: {
          label: {
            en: 'Refrigerated Front Compartment',
            ar: 'حجرة التبريد الأمامية'
          },
          image: 'images/2365//RefrigeratedFrontCompartment.png',
          title: {
            en: 'Refrigerated Front Compartment',
            ar: 'حجرة التبريد الأمامية'
          },
          desc: {
            en: 'Front Center Console with Built-In Refrigerated Storage',
            ar: 'وحدة التحكم المركزية الأمامية مع وحدة تخزين مبردة مدمجة'
          },
          type: 'interior'
        },
        sdicuyai452l: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلوماتي ترفيهي بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال محسّن'
          },
          type: 'interior'
        },
        '3re5wmqz3dw5': {
          label: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        '2hc2o7cuwka9': {
          label: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          image: 'images/2365/information_center.png',
          title: {
            en: 'Steering Column Electronic shifter',
            ar: 'عمود التوجيه ناقل الحركة الإلكتروني'
          },
          desc: {
            en: 'Advanced Electronic Column Shifter',
            ar: 'ناقل عمود إلكتروني متقدم'
          },
          type: 'interior'
        },
        '2je8obbpqtv4': {
          label: {
            en: 'Steering Wheel Paddle Shifters',
            ar: 'مبدلات السرعات في عجلة القيادة'
          },
          image: 'images/2365/information_center.png',
          title: {
            en: 'Steering Wheel Paddle Shifters',
            ar: 'مبدلات السرعات في عجلة القيادة'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        gx1i3vxge08y: {
          label: {
            en: 'Black Grill Design',
            ar: 'تصميم الشواية السوداء'
          },
          image: 'images/2365//BlackGrilleDesign.png',
          title: {
            en: 'Black Grill Design',
            ar: 'تصميم الشواية السوداء'
          },
          desc: {
            en: 'Bold Black Grille with Street Style Front Bumper',
            ar: 'شبكة أمامية سوداء جريئة مع مصد أمامي على طراز الشارع'
          },
          type: 'exterior'
        },
        zja8v4elqyko: {
          label: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'New Front Facia',
            ar: 'واجهة أمامية جديدة'
          },
          desc: {
            en: 'Signature LED Front Lighting',
            ar: 'إضاءة أمامية LED مميزة'
          },
          type: 'exterior'
        },
        d66subt9qz7a: {
          label: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'New Rear Facia',
            ar: 'واجهة خلفية جديدة'
          },
          desc: {
            en: 'LED Tail Lamps',
            ar: 'مصابيح خلفية LED'
          },
          type: 'exterior'
        },
        ai6x6p4bqkhj: {
          label: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          image: 'images/2365//SGF.png',
          title: {
            en: '22" Wheels',
            ar: 'عجلات مقاس 22 بوصة'
          },
          desc: {
            en: 'Performance 22" High-Gloss Black Wheels',
            ar: 'عجلات عالية الأداء قياس 22 بوصة باللون الأسود اللامع'
          },
          type: 'exterior'
        },
        '4bt3sofsvx58': {
          label: {
            en: 'Black Bowtie and Badging',
            ar: 'ربطة عنق سوداء وشارة'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          title: {
            en: 'Black Bowtie and Badging',
            ar: 'ربطة عنق سوداء وشارة'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '1oq2uhwagoto': {
          label: {
            en: 'Black Roof Carrier ',
            ar: 'حامل السقف الأسود'
          },
          image: 'images/2365//BlackRoofCarrier.png',
          title: {
            en: 'Black Roof Carrier ',
            ar: 'حامل السقف الأسود'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '3n0e27xt3f7y': {
          label: {
            en: 'Front Row Bucket Seats',
            ar: 'مقاعد دلو الصف الأمامي'
          },
          image: 'images/2365/ACC/Bucketseats.png',
          title: {
            en: 'Front Row Bucket Seats',
            ar: 'مقاعد دلو الصف الأمامي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        ofq9cjmv0dio: {
          label: {
            en: 'Jet Black and Victory Red Leather seats',
            ar: 'مقاعد جلدية باللون الأسود النفاث والأحمر النصر'
          },
          image: 'images/2365/Interiors/RST Interior.jpg',
          title: {
            en: 'Jet Black and Victory Red Leather seats',
            ar: 'مقاعد جلدية باللون الأسود النفاث والأحمر النصر'
          },
          desc: {
            en: 'Performance-Inspired Jet Black Leather & Victory Red Stitching',
            ar: 'جلد أسود نفاث مستوحى من الأداء وخياطة باللون الأحمر النصر'
          },
          type: 'interior'
        },
        '80a8vikbz9mc': {
          label: {
            en: 'Refrigerated Front Compartment',
            ar: 'حجرة التبريد الأمامية'
          },
          image: 'images/2365//RefrigeratedFrontCompartment.png',
          title: {
            en: 'Refrigerated Front Compartment',
            ar: 'حجرة التبريد الأمامية'
          },
          desc: {
            en: 'Front Center Console with Built-In Refrigerated Storage',
            ar: 'وحدة التحكم المركزية الأمامية مع وحدة تخزين مبردة مدمجة'
          },
          type: 'interior'
        },
        goge77bzjpog: {
          label: {
            en: 'Light and Sensitive Inside Rear View Mirror',
            ar: 'مرآة الرؤية الخلفية الداخلية خفيفة وحساسة'
          },
          image: 'images/2365/irvm.png',
          title: {
            en: 'Light and Sensitive Inside Rear View Mirror',
            ar: 'مرآة الرؤية الخلفية الداخلية خفيفة وحساسة'
          },
          desc: {
            en: 'Auto-Dimming Rearview Mirror with Light Sensitivity',
            ar: 'مرآة الرؤية الخلفية ذات التعتيم التلقائي مع حساسية الضوء'
          },
          type: 'interior'
        },
        '3hptb1jmkv9t': {
          label: {
            en: 'Front Wireless Charger',
            ar: 'شاحن لاسلكي أمامي'
          },
          image: 'images/2365//FrontWirelessCharger.png',
          title: {
            en: 'Front Wireless Charger',
            ar: 'شاحن لاسلكي أمامي'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        '4xrp3r6xunkh': {
          label: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          image: 'images/2365//SII.png',
          title: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          desc: {
            en: '18" Bright Silver Wheels',
            ar: 'عجلات فضية لامعة مقاس 18 بوصة'
          },
          type: 'exterior'
        },
        lddmksxt2r3x: {
          label: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          image: 'images/2365/LT/BodyColorSideMirrors.png',
          title: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          desc: {
            en: 'Sleek Body-Color Side Mirrors',
            ar: 'مرايا جانبية أنيقة بلون الهيكل'
          },
          type: 'exterior'
        },
        vygkaurpl8hc: {
          label: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          image: 'images/2365/LT/FrontFacia.png',
          title: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          desc: {
            en: 'Front Fascia Designed for Improved Ground Clearance',
            ar: 'تم تصميم الواجهة الأمامية لتحسين الخلوص الأرضي'
          },
          type: 'exterior'
        },
        k5z4vhfjy8wq: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'LED Headlamps Enhance Visibility and Style',
            ar: 'مصابيح LED الأمامية تعزز الرؤية والأناقة'
          },
          type: 'exterior'
        },
        '95z9ngnfewd5': {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/ChromeRoofCarrier.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        a7p16fyqm946: {
          label: {
            en: 'Assist Step',
            ar: 'خطوة مساعدة'
          },
          image: 'images/2365/Premier/AssistStep.png',
          title: {
            en: 'Assist Step',
            ar: 'خطوة مساعدة'
          },
          desc: {
            en: 'Black Assist Step with Chrome Accent',
            ar: 'خطوة مساعدة باللون الأسود مع لمسات من الكروم'
          },
          type: 'exterior'
        },
        lhlx7rjoaop9: {
          label: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          image: 'images/2365/LT/TriZoneFrontandRearAirCondition.png',
          title: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          desc: {
            en: 'Tri-Zone Automatic Climate Control',
            ar: 'نظام التحكم التلقائي في المناخ ثلاثي المناطق'
          },
          type: 'interior'
        },
        iufy8ebzwn55: {
          label: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          image: 'images/2365/Interiors/LT_Cloth Interior.jpg',
          title: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          desc: {
            en: 'Two-Tone Gideon & Very Dark Atmosphere Fabric Seats',
            ar: 'مقاعد قماشية ثنائية اللون من نوع جيديون وغامقة للغاية'
          },
          type: 'interior'
        },
        '07zc5saopyb5': {
          label: {
            en: '11" Digital Driver Information Display',
            ar: 'نظام معلومات السائق مقاس 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Digital Driver Information Display',
            ar: 'نظام معلومات السائق مقاس 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        iuwst0brv0wh: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        '0z5t34x4biok': {
          label: {
            en: 'Onstar and 5G',
            ar: 'Onstar و5G'
          },
          image: 'images/2365/LT/Onstarand5G.png',
          title: {
            en: 'Onstar and 5G',
            ar: 'Onstar و5G'
          },
          desc: {
            en: 'OnStar Connected Services with Built-In 5G Wi-Fi Hotspot',
            ar: 'خدمات OnStar المتصلة مع نقطة اتصال Wi-Fi مدمجة بتقنية 5G'
          },
          type: 'interior'
        },
        eg5mmj97g8xe: {
          label: {
            en: 'Chrome Sill Plates',
            ar: 'ألواح عتبة الكروم'
          },
          image: 'images/2365/ChromeDoorSillPlate.png',
          title: {
            en: 'Chrome Sill Plates',
            ar: 'ألواح عتبة الكروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        kj9lo5riyd1n: {
          label: {
            en: 'Liftgate',
            ar: 'باب خلفي'
          },
          image: 'images/2365/Liftgate.png',
          title: {
            en: 'Liftgate',
            ar: 'باب خلفي'
          },
          desc: {
            en: 'Powered and Height Programmable Rear Liftgate',
            ar: 'باب خلفي كهربائي وقابل للبرمجة حسب الارتفاع'
          },
          type: 'interior'
        },
        sf21sgxeu6ko: {
          label: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          image: 'images/2365//SII.png',
          title: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          desc: {
            en: '18" Bright Silver Wheels',
            ar: 'عجلات فضية لامعة مقاس 18 بوصة'
          },
          type: 'exterior'
        },
        yp4i3p3de1f5: {
          label: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          image: 'images/2365/LT/BodyColorSideMirrors.png',
          title: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          desc: {
            en: 'Sleek Body-Color Side Mirrors',
            ar: 'مرايا جانبية أنيقة بلون الهيكل'
          },
          type: 'exterior'
        },
        yvg1m8p6pndx: {
          label: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          image: 'images/2365/LT/FrontFacia.png',
          title: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          desc: {
            en: 'Front Fascia Designed for Improved Ground Clearance',
            ar: 'تم تصميم الواجهة الأمامية لتحسين الخلوص الأرضي'
          },
          type: 'exterior'
        },
        eg09maxszliz: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: 'LED Headlamps Enhance Visibility and Style',
            ar: 'مصابيح LED الأمامية تعزز الرؤية والأناقة'
          },
          type: 'exterior'
        },
        qynhf9eb50dh: {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/ChromeRoofCarrier.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        ksocutusfs7r: {
          label: {
            en: 'Assist Step',
            ar: 'خطوة مساعدة'
          },
          image: 'images/2365/Premier/AssistStep.png',
          title: {
            en: 'Assist Step',
            ar: 'خطوة مساعدة'
          },
          desc: {
            en: 'Black Assist Step with Chrome Accent',
            ar: 'خطوة مساعدة باللون الأسود مع لمسات من الكروم'
          },
          type: 'exterior'
        },
        '7pxtwa8ynuz1': {
          label: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          image: 'images/2365/LT/TriZoneFrontandRearAirCondition.png',
          title: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          desc: {
            en: 'Tri-Zone Automatic Climate Control',
            ar: 'نظام التحكم التلقائي في المناخ ثلاثي المناطق'
          },
          type: 'interior'
        },
        dc9ffp35wvdd: {
          label: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          image: 'images/2365/Interiors/LT_Cloth Interior.jpg',
          title: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          desc: {
            en: 'Two-Tone Gideon & Very Dark Atmosphere Fabric Seats',
            ar: 'مقاعد قماشية ثنائية اللون من نوع جيديون وغامقة للغاية'
          },
          type: 'interior'
        },
        '7ihmnpfmjain': {
          label: {
            en: '11" Digital Driver Information Display',
            ar: 'نظام معلومات السائق مقاس 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Digital Driver Information Display',
            ar: 'نظام معلومات السائق مقاس 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        jl9mgtoi4fpn: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        v9x8tbkmmr7f: {
          label: {
            en: 'Onstar and 5G',
            ar: 'Onstar و5G'
          },
          image: 'images/2365/LT/Onstarand5G.png',
          title: {
            en: 'Onstar and 5G',
            ar: 'Onstar و5G'
          },
          desc: {
            en: 'OnStar Connected Services with Built-In 5G Wi-Fi Hotspot',
            ar: 'خدمات OnStar المتصلة مع نقطة اتصال Wi-Fi مدمجة بتقنية 5G'
          },
          type: 'interior'
        },
        sbtsc5srgjg3: {
          label: {
            en: 'Chrome Sill Plates',
            ar: 'ألواح عتبة الكروم'
          },
          image: 'images/2365/ChromeDoorSillPlate.png',
          title: {
            en: 'Chrome Sill Plates',
            ar: 'ألواح عتبة الكروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'interior'
        },
        qjqmesrzf9i7: {
          label: {
            en: 'Liftgate',
            ar: 'باب خلفي'
          },
          image: 'images/2365/Liftgate.png',
          title: {
            en: 'Liftgate',
            ar: 'باب خلفي'
          },
          desc: {
            en: 'Powered and Height Programmable Rear Liftgate',
            ar: 'باب خلفي كهربائي وقابل للبرمجة حسب الارتفاع'
          },
          type: 'interior'
        },
        jfc6k9282sps: {
          label: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          image: 'images/2365//RCV.png',
          title: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          desc: {
            en: '18" Bright Silver Aluminum Wheels',
            ar: 'عجلات ألومنيوم فضية لامعة مقاس 18 بوصة'
          },
          type: 'exterior'
        },
        d8iqszv1qgtg: {
          label: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          image: 'images/2365/LT/BodyColorSideMirrors.png',
          title: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        l4jlplnah2pk: {
          label: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          image: 'images/2365/LT/FrontFacia.png',
          title: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          desc: {
            en: 'Front Fascia Designed for Improved Ground Clearance',
            ar: 'تم تصميم الواجهة الأمامية لتحسين الخلوص الأرضي'
          },
          type: 'exterior'
        },
        '8ih09bnziphj': {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '0rb2gy4ywuo8': {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/ChromeRoofCarrier.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        ns2sioa1qkk2: {
          label: {
            en: 'Rear Fascia',
            ar: 'اللفافة الخلفية'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'Rear Fascia',
            ar: 'اللفافة الخلفية'
          },
          desc: {
            en: 'LED Tail Lamps',
            ar: 'مصابيح خلفية LED'
          },
          type: 'exterior'
        },
        xgabr5lafxj5: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        zf9hwe5w90h7: {
          label: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        s47kkorg502l: {
          label: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          image: 'images/2365/Interiors/LS Interior.jpg',
          title: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          desc: {
            en: 'Two-Tone Gideon & Very Dark Atmosphere Interior with Cloth Seats',
            ar: 'تصميم داخلي بلونين من Gideon وأجواء داكنة للغاية مع مقاعد من القماش'
          },
          type: 'interior'
        },
        grprosxs87va: {
          label: {
            en: 'Bench Front Seats',
            ar: 'مقاعد أمامية بنش'
          },
          image: 'images/2365/LS/FrontBenchconsole.png',
          title: {
            en: 'Bench Front Seats',
            ar: 'مقاعد أمامية بنش'
          },
          desc: {
            en: 'Front Bench Seating Configuration',
            ar: 'تكوين مقاعد المقاعد الأمامية'
          },
          type: 'interior'
        },
        '3sa90kby1ubz': {
          label: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          image: 'images/2365/LT/TriZoneFrontandRearAirCondition.png',
          title: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          desc: {
            en: 'Tri-Zone Automatic Climate Control',
            ar: 'نظام التحكم التلقائي في المناخ ثلاثي المناطق'
          },
          type: 'interior'
        },
        '29kx4ja7eg7r': {
          label: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          image: 'images/2365//RCV.png',
          title: {
            en: '18" Wheels',
            ar: 'عجلات مقاس 18 بوصة'
          },
          desc: {
            en: '18" Bright Silver Aluminum Wheels',
            ar: 'عجلات ألومنيوم فضية لامعة مقاس 18 بوصة'
          },
          type: 'exterior'
        },
        '3ud49gqncb2e': {
          label: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          image: 'images/2365/LT/BodyColorSideMirrors.png',
          title: {
            en: 'Body Color Side Mirrors',
            ar: 'مرايا جانبية بلون الهيكل'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        '1jhh1m7485kl': {
          label: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          image: 'images/2365/LT/FrontFacia.png',
          title: {
            en: 'High Approach Angle Front Facia',
            ar: 'واجهة أمامية بزاوية اقتراب عالية'
          },
          desc: {
            en: 'Front Fascia Designed for Improved Ground Clearance',
            ar: 'تم تصميم الواجهة الأمامية لتحسين الخلوص الأرضي'
          },
          type: 'exterior'
        },
        einjxmvv1rxf: {
          label: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          image: 'images/2365//LEDHeadlamps.png',
          title: {
            en: 'LED Headlamps',
            ar: 'مصابيح أمامية LED'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        mw3hss4999re: {
          label: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          image: 'images/2365/Premier/ChromeRoofCarrier.png',
          title: {
            en: 'Chrome Roof Carrier',
            ar: 'حامل سقف كروم'
          },
          desc: {
            en: '',
            ar: ''
          },
          type: 'exterior'
        },
        b7xdnq4ptvlx: {
          label: {
            en: 'Rear Fascia',
            ar: 'اللفافة الخلفية'
          },
          image: 'images/2365/Taillamp_Feature_Thumbnail.jpg',
          title: {
            en: 'Rear Fascia',
            ar: 'اللفافة الخلفية'
          },
          desc: {
            en: 'LED Tail Lamps',
            ar: 'مصابيح خلفية LED'
          },
          type: 'exterior'
        },
        bf5spqe7qghc: {
          label: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          image: 'images/2365/Premier/DiagonalInfotainmentScreen.png',
          title: {
            en: '17.7" Diagonal Infotainment Screen',
            ar: 'شاشة معلوماتية ترفيهية قطرية مقاس 17.7 بوصة'
          },
          desc: {
            en: '17.7" Diagonal Touchscreen Infotainment System with Enhanced Connectivity',
            ar: 'نظام معلومات وترفيه بشاشة لمس قطرية مقاس 17.7 بوصة مع اتصال مُحسّن'
          },
          type: 'interior'
        },
        '2kzy6a0jhsu2': {
          label: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          image: 'images/2365/Premier/DriverInformationCentre.png',
          title: {
            en: '11" Driver Information Centre',
            ar: 'مركز معلومات السائق 11 بوصة'
          },
          desc: {
            en: '11" Digital Driver Information Display with Customizable Views',
            ar: 'شاشة عرض معلومات السائق الرقمية مقاس 11 بوصة مع طرق عرض قابلة للتخصيص'
          },
          type: 'interior'
        },
        zq18jgsgj1sc: {
          label: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          image: 'images/2365/Interiors/LS Interior.jpg',
          title: {
            en: 'Gideon/ Very Dark Atmosphere Seats',
            ar: 'جيديون / مقاعد ذات أجواء مظلمة للغاية'
          },
          desc: {
            en: 'Two-Tone Gideon & Very Dark Atmosphere Interior with Cloth Seats',
            ar: 'تصميم داخلي بلونين من Gideon وأجواء داكنة للغاية مع مقاعد من القماش'
          },
          type: 'interior'
        },
        '4cq85subl38y': {
          label: {
            en: 'Bench Front Seats',
            ar: 'مقاعد أمامية بنش'
          },
          image: 'images/2365/LS/FrontBenchconsole.png',
          title: {
            en: 'Bench Front Seats',
            ar: 'مقاعد أمامية بنش'
          },
          desc: {
            en: 'Front Bench Seating Configuration',
            ar: 'تكوين مقاعد المقاعد الأمامية'
          },
          type: 'interior'
        },
        '': {
          label: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          image: 'images/2365/LT/TriZoneFrontandRearAirCondition.png',
          title: {
            en: 'Tri Zone Front and Rear Air Condition',
            ar: 'مكيف هواء ثلاثي المناطق أمامي وخلفي'
          },
          desc: {
            en: 'Tri-Zone Automatic Climate Control',
            ar: 'نظام التحكم التلقائي في المناخ ثلاثي المناطق'
          },
          type: 'interior'
        }
      },
      accessories: {
        j8gbkfpqangk: {
          label: {
            en: 'Engine, 6.2L EcoTec3 V8',
            ar: 'المحرك 6.2 لتر EcoTec3 V8'
          },
          image: 'images/2365/ACC/Engine62L.avif',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 8217,
            UAE: 8064,
            OMR: 855,
            BHD: 833,
            QAR: 7976,
            JOD: 1556,
            KW: 679,
            IQD: 2191,
            LBP: 2191,
            default: 8064,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'L87',
          brandItemCode: 'AC37',
        },
        wdbc64xihtb2: {
          label: {
            en: 'Default Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        as1rluq401n7: {
          label: {
            en: 'Acc Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8k3kqiy0rpwl': {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//R4C.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vy9a4fqiu3mi: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        u20m3d5j71vd: {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        f4jur4qda1iy: {
          label: {
            en: 'Advanced Security Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/solid_sunroof.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3l56f6t8yrhk': {
          label: {
            en: 'Advanced Security Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/solid_sunroof.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0agqewqxtxza': {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        '8m3lbfnbgjgt': {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        an4yb9ia2pck: {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        ck165ih65lno: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        xfkdiah0cnde: {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/BlackNameplatesPremier.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        juaj9a2rptxz: {
          label: {
            en: 'Illuminated Sill Plates',
            ar: 'عتبات مضاءة'
          },
          image: 'images/2365/ACC/IlluminatedSillPlates.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S0M',
          excludeFromRecommendation: true,
          brandItemCode: 'AC23',
        },
        tapbu8okt0uj: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        '4by5qhwuy594': {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mr7qy5fo5qhm: {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wf9709z90ina: {
          label: {
            en: '22" 6 Spoke Low Gloss Black Wheel',
            ar: 'عجلات سوداء منخفضة اللمعان بستة أضلاع مقاس 22 بوصة'
          },
          image: 'images/2365/ACC/BlackWheels.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 8966,
            UAE: 8799,
            OMR: 933,
            BHD: 909,
            QAR: 8703,
            JOD: 1698,
            KW: 741,
            IQD: 2391,
            LBP: 2391,
            default: 8799,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SEV',
          brandItemCode: 'AC41',
        },
        '4fjgp644j954': {
          label: {
            en: 'Air Ride Suspension',
            ar: 'تعليق ركوب الهواء'
          },
          image: 'images/2365/Premier/Suspension.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 3293,
            UAE: 3232,
            OMR: 343,
            BHD: 334,
            QAR: 3197,
            JOD: 624,
            KW: 272,
            IQD: 878,
            LBP: 878,
            default: 3232,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'F47',
          brandItemCode: 'AC42',
        },
        '0wu0cglndrqc': {
          label: {
            en: 'Engine, 6.2L EcoTec3 V8',
            ar: 'المحرك 6.2 لتر EcoTec3 V8'
          },
          image: 'images/2365/ACC/Engine62L.avif',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 8217,
            UAE: 8064,
            OMR: 855,
            BHD: 833,
            QAR: 7976,
            JOD: 1556,
            KW: 679,
            IQD: 2191,
            LBP: 2191,
            default: 8064,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'L87',
          brandItemCode: 'AC37',
        },
        u4l2ea4u591d: {
          label: {
            en: 'Default Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zdstqruyal13: {
          label: {
            en: 'Acc Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qz1xkch699vj: {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//R4C.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ouphufegebch: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        '73ocxc2sdp6r': {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        '537stl5m1lxj': {
          label: {
            en: 'Advanced Security Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/solid_sunroof.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wgwpi0cpuvm1: {
          label: {
            en: 'Advanced Security Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/solid_sunroof.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '220sgfu4ue0x': {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        n8yi3roj6gyz: {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        jcnivvs85wga: {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        ygbf6qmc6kke: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        grvysusnjiab: {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/BlackNameplatesPremier.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        atupcjelnx02: {
          label: {
            en: 'Illuminated Sill Plates',
            ar: 'عتبات مضاءة'
          },
          image: 'images/2365/ACC/IlluminatedSillPlates.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S0M',
          excludeFromRecommendation: true,
          brandItemCode: 'AC23',
        },
        u7mrz45son0v: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        hchvsg67r27z: {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '59w2od9p35l4': {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        wf9709z90ina23: {
          label: {
            en: 'Air Ride Suspension',
            ar: 'تعليق ركوب الهواء'
          },
          image: 'images/2365/Premier/Suspension.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 3293,
            UAE: 3232,
            OMR: 343,
            BHD: 334,
            QAR: 3197,
            JOD: 624,
            KW: 272,
            IQD: 878,
            LBP: 878,
            default: 3232,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'F47',
          brandItemCode: 'AC42',
        },
        wf9709z90ina34: {
          label: {
            en: 'Engine, 6.2L EcoTec3 V8',
            ar: 'المحرك 6.2 لتر EcoTec3 V8'
          },
          image: 'images/2365/ACC/Engine62L.avif',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 8217,
            UAE: 8064,
            OMR: 855,
            BHD: 833,
            QAR: 7976,
            JOD: 1556,
            KW: 679,
            IQD: 2191,
            LBP: 2191,
            default: 8064,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'L87',
          brandItemCode: 'AC37',
        },
        '63y0kvv7xxr2': {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        ocbn9q1rvely: {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        ctvxw4vspn8n: {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        asyqhfvwphz6: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        '6wndqn4xdcpe': {
          label: {
            en: 'Illuminated Sill Plates',
            ar: 'عتبات مضاءة'
          },
          image: 'images/2365/ACC/IlluminatedSillPlates.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S0M',
          excludeFromRecommendation: true,
          brandItemCode: 'AC23',
        },
        fgzwkqgju86l: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        '2k5neto3ntiw': {
          label: {
            en: 'Roof',
            ar: 'Roof'
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ze9qelaq0ea7: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة سقف'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3U',
          brandItemCode: 'OP01',
        },
        kxve94jfxnjn: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ow69hy698vfs: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zpdxf0ojy1ys: {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//R4M.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        filx1puwuu5x: {
          label: {
            en: '20" High Gloss Black Aggressive Aluminium',
            ar: '20 بوصة من الألومنيوم القوي باللون الأسود اللامع'
          },
          image: 'images/2365//NZW.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 4611,
            UAE: 4525,
            OMR: 480,
            BHD: 467,
            QAR: 4476,
            JOD: 873,
            KW: 381,
            IQD: 1230,
            LBP: 1230,
            default: 4525,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZW',
          brandItemCode: 'OP09',
        },
        o5djqpf4c2zz: {
          label: {
            en: '20" Machined Aluminum With Charcoal Pockets',
            ar: '20 بوصة من الألومنيوم المخرط مع جيوب الفحم'
          },
          image: 'images/2365//NZV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 6258,
            UAE: 6141,
            OMR: 651,
            BHD: 634,
            QAR: 6074,
            JOD: 1185,
            KW: 517,
            IQD: 1669,
            LBP: 1669,
            default: 6141,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZV',
          brandItemCode: 'OP10',
        },
        '786orlp7brpi': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        do59cklquwhw: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '50bp9axsjq0b': {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8e344wbsrdwe': {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        dyotlhu6y13v: {
          label: {
            en: 'Rear entertainment screen',
            ar: 'فتحة السقف'
          },
          image: 'images/2365//Packages/z71_comfort_package_rear_screen.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'UW9',
          brandItemCode: 'AC46',
        },
        '7d7txgmtnv90': {
          label: {
            en: 'Memory Seats',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AUN',
          brandItemCode: 'AC47',
        },
        uzlkrbqqpemr: {
          label: {
            en: 'Memory Seats Default',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        z0at7jbpskb3: {
          label: {
            en: 'Solid Roof',
            ar: 'سقف صلب'
          },
          image: 'images/2365/solid_sunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          brandItemCode: 'AC215',
        },
        rye7d6mc16r0: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3U',
          brandItemCode: 'OP01',
        },
        emj5q4fdnout: {
          label: {
            en: 'Bench seats',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Seats',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AZ3',
          brandItemCode: 'AC36',
        },
        '4c61rda34zee': {
          label: {
            en: 'Bucket seats',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/ACC/Bucketseats.png',
          category: {
            en: 'Seats',
            ar: 'سَطح'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            IQD: 1436,
            LBP: 1436,
            default: 5285,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC48',
        },
        '4tihcmimu2ok': {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//SGF.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fpn25sp5gphy: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        '9oh6k48k8cxx': {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        jqxngcgzuv9b: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '8wr423b2htlh': {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gfbqa4vsn2wr: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        kfu51ifirrz4: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9kyqypnk8s04': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        e3cpetqtxy02: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        tibyw2x77m3a: {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        ujk0lpogtp9a: {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        '2jfy7wddw2lw': {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/BlackNameplatesRST.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        rvqnxfvkbwf6: {
          label: {
            en: 'Black Exhaust Tip',
            ar: 'طرف عادم أسود'
          },
          image: 'images/2365/ACC/BlackExhaustTip.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VTA',
          brandItemCode: 'AC24',
        },
        '0bsfsmin2z34': {
          label: {
            en: 'advanced_trailering_package',
            ar: 'advanced_trailering_package'
          },
          image: 'images/2365/Packages/Advanced Trailering Package.jpg',
          category: {
            en: '',
            ar: ''
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vr811gju19tq: {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        zano5i0m9vpm: {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        jmfvwre4e5wl: {
          label: {
            en: 'Memory Seats Default',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 303.82,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            LBP: 83,
            IQD: 83,
            default: 303.82,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        dlanz97wztgs: {
          label: {
            en: 'DEFAULT_HIGH_APPROACH_FASCIA',
            ar: 'DEFAULT_HIGH_APPROACH_FASCIA'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        x0ewn8d547lf: {
          label: {
            en: 'Street Front Fascia',
            ar: 'واجهة الشارع الأمامي'
          },
          image: 'images/2365/LT/StreetFtFascia.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WTX',
          brandItemCode: 'AC49',
        },
        '8o2g0vdgczzb': {
          label: {
            en: 'Roof',
            ar: 'Roof'
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        fpfkrtu6uh3e: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة سقف'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3U',
          brandItemCode: 'OP01',
        },
        '76yy5aew7ka9': {
          label: {
            en: 'Front Bench Console',
            ar: 'Front Bench Console'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        xnm43hp741wy: {
          label: {
            en: 'Front Bucket Console without Wireless charger',
            ar: 'وحدة التحكم الأمامية بدون شاحن لاسلكي'
          },
          image: 'images/2365/LT Bucket Console.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 1153,
            UAE: 1131,
            OMR: 120,
            BHD: 117,
            QAR: 1119,
            JOD: 218,
            KW: 95,
            IQD: 307,
            LBP: 307,
            default: 1131,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        lmzjn7akfeiz: {
          label: {
            en: 'Front Bucket Console with Wireless charger',
            ar: 'وحدة التحكم الأمامية مع شاحن لاسلكي'
          },
          image: 'images/2365/LT Bucket Console.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 1153,
            UAE: 1131,
            OMR: 120,
            BHD: 117,
            QAR: 1119,
            JOD: 218,
            KW: 95,
            IQD: 307,
            LBP: 307,
            default: 1131,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC50',
        },
        '1mavqo62dsmu': {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//RCV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3baaofq60q2o': {
          label: {
            en: '20" High Gloss Black Aggressive Aluminium',
            ar: '20 بوصة من الألومنيوم القوي باللون الأسود اللامع'
          },
          image: 'images/2365//NZW.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 4611,
            UAE: 4525,
            OMR: 480,
            BHD: 467,
            QAR: 4476,
            JOD: 873,
            KW: 381,
            IQD: 1230,
            LBP: 1230,
            default: 4525,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZW',
          brandItemCode: 'OP09',
        },
        psqnhs79c4zr: {
          label: {
            en: '20" Machined Aluminum With Charcoal Pockets',
            ar: '20 بوصة من الألومنيوم المخرط مع جيوب الفحم'
          },
          image: 'images/2365//NZV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 6258,
            UAE: 6141,
            OMR: 651,
            BHD: 634,
            QAR: 6074,
            JOD: 1185,
            KW: 517,
            IQD: 1669,
            LBP: 1669,
            default: 6141,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZV',
          brandItemCode: 'OP10',
        },
        eaaz58mmbiz8: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        fuw0owjjsik1: {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        '4jy3fhzypv8n': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '9ahip044pcui': {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        h0o57uwnp9eq: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        qrlyo2fi1cz5: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gr212v81y9j8: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ilcd1ac7uwte: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0liobyyvrpud': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '22r2iguvwna5': {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '5y8auet27vap': {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        q2jxwuubfmts: {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        '2tgsrkgxlq2n': {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        mu1nnlb77i13: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        q0kpkldh7h9h: {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/BlackNameplatesLT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        '9bh9bo8nka30': {
          label: {
            en: 'Illuminated Sill Plates',
            ar: 'عتبات مضاءة'
          },
          image: 'images/2365/ACC/IlluminatedSillPlates.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S0M',
          excludeFromRecommendation: true,
          brandItemCode: 'AC23',
        },
        s6yov9pn3z94: {
          label: {
            en: 'Black Exhaust Tip',
            ar: 'طرف عادم أسود'
          },
          image: 'images/2365/ACC/BlackExhaustTip.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VTA',
          brandItemCode: 'AC24',
        },
        flcd4tk3152i: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        b47ijnigxias: {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        p2k3ocymx5b6: {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ka15u2dvchya: {
          label: {
            en: 'DEFAULT_MANUAL_IRVM_LT',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        r1n54l44td0x: {
          label: {
            en: '22" 6 Spoke Low Gloss Black Wheel',
            ar: 'عجلات سوداء منخفضة اللمعان بستة أضلاع مقاس 22 بوصة'
          },
          image: 'images/2365/ACC/BlackWheels.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 8966,
            UAE: 8799,
            OMR: 933,
            BHD: 909,
            QAR: 8703,
            JOD: 1698,
            KW: 741,
            IQD: 2391,
            LBP: 2391,
            default: 8799,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SEV',
          brandItemCode: 'AC41',
        },
        tautgpnlntno: {
          label: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية مدفأة'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            IQD: 72,
            LBP: 72,
            default: 265,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KA1',
          brandItemCode: 'OP18',
        },
        '621q9g1g6arh': {
          label: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية مدفأة'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            IQD: 72,
            LBP: 72,
            default: 265,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KA1',
          brandItemCode: 'OP18',
        },
        l9o7n2dgqf4r: {
          label: {
            en: '8-Way Power Front Passenger Seat',
            ar: 'مقعد الراكب الأمامي كهربائياً بـ 8 وضعيات'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            IQD: 72,
            LBP: 72,
            default: 265,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A7K',
          brandItemCode: 'AC52',
        },
        mtjkzka4hyry: {
          label: {
            en: 'Memory Seats',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_lt.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AUN',
          brandItemCode: 'AC47',
        },
        '6wqyiqzo0qbj': {
          label: {
            en: 'Memory Seats Default',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        mh8yll2ghfym: {
          label: {
            en: 'DEFAULT_HIGH_APPROACH_FASCIA',
            ar: 'DEFAULT_HIGH_APPROACH_FASCIA'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1mvpa42b7x58': {
          label: {
            en: 'Street Front Fascia',
            ar: 'واجهة الشارع الأمامي'
          },
          image: 'images/2365/LT/StreetFtFascia.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WTX',
          brandItemCode: 'AC49',
        },
        bmut9gj383kz: {
          label: {
            en: 'Roof',
            ar: 'Roof'
          },
          image: '',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0zwxk45hp9q3': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة سقف'
          },
          image: 'images/2365/Premier/PowerPanaromicSunroof.png',
          category: {
            en: 'Roof',
            ar: 'سَطح'
          },
          price: {
            KSA: 4940,
            UAE: 4848,
            OMR: 514,
            BHD: 501,
            QAR: 4795,
            JOD: 935,
            KW: 408,
            IQD: 1317,
            LBP: 1317,
            default: 4848,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3U',
          brandItemCode: 'OP01',
        },
        zwlfrhv0um37: {
          label: {
            en: 'Front Bench Console',
            ar: 'Front Bench Console'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        pg7vdt72cjtt: {
          label: {
            en: 'Front Bucket Console without Wireless charger',
            ar: 'وحدة التحكم الأمامية بدون شاحن لاسلكي'
          },
          image: 'images/2365/LT Bucket Console.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 1153,
            UAE: 1131,
            OMR: 120,
            BHD: 117,
            QAR: 1119,
            JOD: 218,
            KW: 95,
            IQD: 307,
            LBP: 307,
            default: 1131,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        s2zs0r60v7n1: {
          label: {
            en: 'Front Bucket Console with Wireless charger',
            ar: 'وحدة التحكم الأمامية مع شاحن لاسلكي'
          },
          image: 'images/2365/LT Bucket Console.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 1153,
            UAE: 1131,
            OMR: 120,
            BHD: 117,
            QAR: 1119,
            JOD: 218,
            KW: 95,
            IQD: 307,
            LBP: 307,
            default: 1131,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A50',
          brandItemCode: 'AC50',
        },
        '3lxqq538zwmd': {
          label: {
            en: '22" Bright Machined High Gloss Painted',
            ar: '22 بوصة مطلية بطبقة لامعة عالية الجودة'
          },
          image: 'images/2365//RCV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        txaq2clwjh4b: {
          label: {
            en: '20" High Gloss Black Aggressive Aluminium',
            ar: '20 بوصة من الألومنيوم القوي باللون الأسود اللامع'
          },
          image: 'images/2365//NZW.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 4611,
            UAE: 4525,
            OMR: 480,
            BHD: 467,
            QAR: 4476,
            JOD: 873,
            KW: 381,
            IQD: 1230,
            LBP: 1230,
            default: 4525,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZW',
          brandItemCode: 'OP09',
        },
        jxm0gmjcapku: {
          label: {
            en: '20" Machined Aluminum With Charcoal Pockets',
            ar: '20 بوصة من الألومنيوم المخرط مع جيوب الفحم'
          },
          image: 'images/2365//NZV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 6258,
            UAE: 6141,
            OMR: 651,
            BHD: 634,
            QAR: 6074,
            JOD: 1185,
            KW: 517,
            IQD: 1669,
            LBP: 1669,
            default: 6141,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZV',
          brandItemCode: 'OP10',
        },
        qxnu1ufa5f2l: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        ookmqq6bquqm: {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        '6di4c84i0dy0': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        yn2ooyds378y: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4n7vrcx493gx': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mwawbqfdlt3b: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        gx72l1mz30pe: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '2plajrmvths5': {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '1rx23hesytaa': {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oakqa7avxhmq: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ptvlbx4oakbu: {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        lrpq8u8zpdb1: {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        '2f2ldbh1iacq': {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        lsc3om940tkg: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        k1tgm4d8un58: {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/BlackNameplatesLT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        '3fuv04x49k8u': {
          label: {
            en: 'Illuminated Sill Plates',
            ar: 'عتبات مضاءة'
          },
          image: 'images/2365/ACC/IlluminatedSillPlates.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'S0M',
          excludeFromRecommendation: true,
          brandItemCode: 'AC23',
        },
        czxgco8l62fv: {
          label: {
            en: 'Black Exhaust Tip',
            ar: 'طرف عادم أسود'
          },
          image: 'images/2365/ACC/BlackExhaustTip.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VTA',
          brandItemCode: 'AC24',
        },
        gdi5279zy1a2: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        t7nql3o89wl9: {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        iealvlwfdrg1: {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        iealvlwfdrg12: {
          label: {
            en: 'DEFAULT_MANUAL_IRVM_LT',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        oiv75228zve7: {
          label: {
            en: '22" 6 Spoke Low Gloss Black Wheel',
            ar: 'عجلات سوداء منخفضة اللمعان بستة أضلاع مقاس 22 بوصة'
          },
          image: 'images/2365/ACC/BlackWheels.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 8966,
            UAE: 8799,
            OMR: 933,
            BHD: 909,
            QAR: 8703,
            JOD: 1698,
            KW: 741,
            IQD: 2391,
            LBP: 2391,
            default: 8799,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SEV',
          brandItemCode: 'AC41',
        },
        ig3l66uxabo4: {
          label: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية مدفأة'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            IQD: 72,
            LBP: 72,
            default: 265,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KA1',
          brandItemCode: 'OP18',
        },
        'ig3l66uxabo4-my26': {
          label: {
            en: 'Heated Front Seats',
            ar: 'مقاعد أمامية مدفأة'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265.04,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            LBP: 72,
            IQD: 72,
            default: 265.04,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'KA1',
          brandItemCode: 'OP18',
        },
        tautgpnlntnoffwef: {
          label: {
            en: '8-Way Power Front Passenger Seat',
            ar: 'مقعد الراكب الأمامي كهربائياً بـ 8 وضعيات'
          },
          image: 'images/2365/ACC/HeatedFrontSeatsImg.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 270,
            UAE: 265,
            OMR: 28,
            BHD: 27,
            QAR: 262,
            JOD: 51,
            KW: 22,
            IQD: 72,
            LBP: 72,
            default: 265,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'A7K',
          brandItemCode: 'AC52',
        },
        l9o7n2dgqf4r32: {
          label: {
            en: 'Memory Seats',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_lt.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'AUN',
          brandItemCode: 'AC47',
        },
        l9o7n2dgqf4r321: {
          label: {
            en: 'Memory Seats Default',
            ar: 'مقاعد الذاكرة'
          },
          image: 'images/2365/ACC/memory_seat_z71.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 310,
            UAE: 304,
            OMR: 32,
            BHD: 31,
            QAR: 301,
            JOD: 59,
            KW: 26,
            IQD: 83,
            LBP: 83,
            default: 304,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false
        },
        mre7m5eq7pp3: {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        rgm1s2u48s3z: {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        w2einnny5a2l: {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        h1qdprk0kd0p: {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        '8lz29kitnkam': {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/ACC_BlackNameplates_TAHOE.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        '5zc0rx55r9q9': {
          label: {
            en: 'Black Exhaust Tip',
            ar: 'طرف عادم أسود'
          },
          image: 'images/2365/ACC/BlackExhaustTip.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VTA',
          brandItemCode: 'AC24',
        },
        dq9u6gqmbzk1: {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        mydj9h508fxd: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        itbl6bdvdboy: {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        pe7y08bs0zcn: {
          label: {
            en: '18" Bright Silver Painted Aluminium',
            ar: '18 بوصة من الألومنيوم المطلي بالفضة اللامعة'
          },
          image: 'images/2365//RCV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        no2apmxabsv7: {
          label: {
            en: '20" High Gloss Black Aggressive Aluminium',
            ar: '20 بوصة من الألومنيوم القوي باللون الأسود اللامع'
          },
          image: 'images/2365//NZW.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 4611,
            UAE: 4525,
            OMR: 480,
            BHD: 467,
            QAR: 4476,
            JOD: 873,
            KW: 381,
            IQD: 1230,
            LBP: 1230,
            default: 4525,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZW',
          brandItemCode: 'OP09',
        },
        '03hdnt4slw1j': {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        ugyico57fj8q: {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        '2xg5fkuod39v': {
          label: {
            en: 'Front Bench Console',
            ar: 'Front Bench Console'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        vq7tvvssnsow: {
          label: {
            en: 'Front Bucket Console',
            ar: 'وحدة تحكم الجرافة الأمامية'
          },
          image: 'images/2365/LS/FrontBucketConsole.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC53',
        },
        qk56aim53mo1: {
          label: {
            en: 'DEFAULT_HIGH_APPROACH_FASCIA',
            ar: 'DEFAULT_HIGH_APPROACH_FASCIA'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        bz0b6bnb2d3x: {
          label: {
            en: 'Street Front Fascia',
            ar: 'واجهة الشارع الأمامي'
          },
          image: 'images/2365/LT/StreetFtFasciaLS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WTX',
          brandItemCode: 'AC49',
        },
        mfsw9xounf28: {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        o50knbpnx8xn: {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        b9hh0ghhfnsm: {
          label: {
            en: '22" 6 Spoke Low Gloss Black Wheel',
            ar: 'عجلات سوداء منخفضة اللمعان بستة أضلاع مقاس 22 بوصة'
          },
          image: 'images/2365/ACC/BlackWheels.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 8966,
            UAE: 8799,
            OMR: 933,
            BHD: 909,
            QAR: 8703,
            JOD: 1698,
            KW: 741,
            IQD: 2391,
            LBP: 2391,
            default: 8799,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SEV',
          brandItemCode: 'AC41',
        },
        '60cyi4386dh7': {
          label: {
            en: 'Bowtie Logo Puddle Lights',
            ar: 'أضواء البرك بشعار ربطة العنق'
          },
          image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          excludeFromRecommendation: true,
          brandItemCode: 'AC15',
        },
        '3ckvx6gvve67': {
          label: {
            en: 'Black Bowtie Emblems',
            ar: 'شعارات ربطة العنق السوداء'
          },
          image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SFZ',
          brandItemCode: 'AC14',
        },
        '26awwpydj3k4': {
          label: {
            en: 'Floor Mat-Chrome',
            ar: 'سجاد الأرضية - كروم'
          },
          image: 'images/2365/ACC/ACC_MAT.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VAV',
          excludeFromRecommendation: true,
          brandItemCode: 'AC16',
        },
        '4g3h4ls28grj': {
          label: {
            en: 'Illuminated Black Bowtie',
            ar: 'ربطة عنق سوداء مضيئة'
          },
          image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'R88',
          brandItemCode: 'AC18',
        },
        '56cy0d0cnz7u': {
          label: {
            en: 'Black Nameplates',
            ar: 'لوحات تحمل أسماء سوداء'
          },
          image: 'images/2365/ACC/ACC_BlackNameplates_TAHOE.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'RIK',
          brandItemCode: 'AC40',
        },
        '496aqzregvz5': {
          label: {
            en: 'Black Exhaust Tip',
            ar: 'طرف عادم أسود'
          },
          image: 'images/2365/ACC/BlackExhaustTip.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'VTA',
          brandItemCode: 'AC24',
        },
        '4e8rsvm8yulq': {
          label: {
            en: 'Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/ACC/ACC_CargoSillPlate.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'C3Q',
          excludeFromRecommendation: true,
          brandItemCode: 'AC13',
        },
        ukt6vqkkvuhg: {
          label: {
            en: '',
            ar: 'سقف صلب'
          },
          image: '',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3ckvx6gvve79': {
          label: {
            en: '',
            ar: 'فتحة السقف'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5386,
            UAE: 5285,
            OMR: 560,
            BHD: 546,
            QAR: 5228,
            JOD: 1020,
            KW: 445,
            LBP: 1436,
            IQD: 1436,
            default: 5285,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        ij5dojjdjzao: {
          label: {
            en: '18" Bright Silver Painted Aluminium',
            ar: '18 بوصة من الألومنيوم المطلي بالفضة اللامعة'
          },
          image: 'images/2365//RCV.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        xxu51rvhkatu: {
          label: {
            en: '20" High Gloss Black Aggressive Aluminium',
            ar: '20 بوصة من الألومنيوم القوي باللون الأسود اللامع'
          },
          image: 'images/2365//NZW.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 4611,
            UAE: 4525,
            OMR: 480,
            BHD: 467,
            QAR: 4476,
            JOD: 873,
            KW: 381,
            IQD: 1230,
            LBP: 1230,
            default: 4525,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'NZW',
          brandItemCode: 'OP09',
        },
        trux0ouskla2: {
          label: {
            en: '22" Polished Aluminum Alloy',
            ar: 'سبائك ألومنيوم مصقولة مقاس 22 بوصة'
          },
          image: 'images/2365//SII.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 5912,
            UAE: 5802,
            OMR: 615,
            BHD: 599,
            QAR: 5739,
            JOD: 1119,
            KW: 489,
            IQD: 1577,
            LBP: 1577,
            default: 5802,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SII',
          brandItemCode: 'OP04',
        },
        grbqqvyo3nmg: {
          label: {
            en: '22" 6-spoke Carbon Flash Metallic Aluminum With Selective Machining',
            ar: 'عجلات ألمنيوم معدنية كربونية فلاشية مقاس 22 بوصة بستة أضلاع مع معالجة انتقائية'
          },
          image: 'images/2365//SIY.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 7559,
            UAE: 7418,
            OMR: 786,
            BHD: 766,
            QAR: 7337,
            JOD: 1431,
            KW: 625,
            IQD: 2016,
            LBP: 2016,
            default: 7418,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SIY',
          brandItemCode: 'OP05',
        },
        vvjg0ome82jt: {
          label: {
            en: 'Front Bench Console',
            ar: 'Front Bench Console'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '0s5749peesms': {
          label: {
            en: 'Front Bucket Console',
            ar: 'وحدة تحكم الجرافة الأمامية'
          },
          image: 'images/2365/LS/FrontBucketConsole.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'interior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'D07',
          brandItemCode: 'AC53',
        },
        ixr3i03h2o98: {
          label: {
            en: 'DEFAULT_HIGH_APPROACH_FASCIA',
            ar: 'DEFAULT_HIGH_APPROACH_FASCIA'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '3ckvx6gvve78': {
          label: {
            en: 'Street Front Fascia',
            ar: 'واجهة الشارع الأمامي'
          },
          image: 'images/2365/LT/StreetFtFasciaLS.jpg',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'WTX',
          brandItemCode: 'AC49',
        },
        '4e8rsvm8yulq1': {
          label: {
            en: 'Default Cargo Area Sill Plate',
            ar: 'عتبة منطقة الشحن'
          },
          image: 'images/2365/trunk_cargo_net.png',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        '4e8rsvm8yulq12': {
          label: {
            en: 'variant_parts',
            ar: 'عتبة منطقة الشحن'
          },
          image: '',
          category: {
            en: 'Options & Accessories',
            ar: 'مُكَمِّلات'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false
        },
        mr7qy5fo6qhm: {
          label: {
            en: '22" 6 Spoke Low Gloss Black Wheel',
            ar: 'عجلات سوداء منخفضة اللمعان بستة أضلاع مقاس 22 بوصة'
          },
          image: 'images/2365/ACC/BlackWheels.png',
          category: {
            en: 'Alloy Wheels',
            ar: 'عجلات معدنية'
          },
          price: {
            KSA: 8966,
            UAE: 8799,
            OMR: 933,
            BHD: 909,
            QAR: 8703,
            JOD: 1698,
            KW: 741,
            IQD: 2391,
            LBP: 2391,
            default: 8799,
          },
          view: 'exterior',
          cardType: 'cart',
          hasQuantity: false,
          autoIntelRPOCode: 'SEV',
          brandItemCode: 'AC41',
        }
      },
      packages: {
        x7vtyrufcgr0: {
          label: {
            en: 'Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Enhanced Visibility Package includes enhanced visibility features such as a head-up display and rear view camera.',
            ar: 'تتضمن حزمة الرؤية المحسنة ميزات رؤية محسنة مثل شاشة عرض أمامية وكاميرا الرؤية الخلفية.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Heads-Up Display',
                ar: 'شاشة عرض أمامية'
              },
              accessoryDescription: {
                en: 'Heads-Up Display',
                ar: 'شاشة عرض أمامية'
              },
              image: 'images/2365/Packages/premier_enhanced_visibility_package_1.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              image: 'images/2365/Packages/premier_enhanced_visibility_package_0.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'Y74',
          brandItemCode: 'PA13',
        },
        sd9rqxud0aff: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 471,
            UAE: 462,
            OMR: 49,
            BHD: 48,
            QAR: 457,
            JOD: 89,
            KW: 39,
            IQD: 126,
            LBP: 126,
            default: 462,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced security features along with Auto sense power and hands-free lift gate.',
            ar: 'تتضمن الحزمة التكنولوجية ميزات أمان متقدمة بالإضافة إلى نظام الاستشعار التلقائي وبوابة الرفع بدون استخدام اليدين.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Security Package',
                ar: 'حزمة الأمان المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Security Package',
                ar: 'حزمة الأمان المتقدمة'
              },
              image: 'images/2365/Packages/Technology Package.jpg',
              price: {
                KSA: 313,
                UAE: 307,
                OMR: 33,
                BHD: 32,
                QAR: 304,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 307,
              }
            },
            {
              accessoryTitle: {
                en: 'Auto Sense Power and Hands-free lift gate',
                ar: 'باب خلفي كهربائي مع استشعار تلقائي وفتح الباب بدون استخدام اليدين'
              },
              accessoryDescription: {
                en: 'Auto Sense Power and Hands-free lift gate',
                ar: 'باب خلفي كهربائي مع استشعار تلقائي وفتح الباب بدون استخدام اليدين'
              },
              image: 'images/2365/Packages/Technology Package.jpg',
              price: {
                KSA: 158,
                UAE: 155,
                OMR: 16,
                BHD: 16,
                QAR: 153,
                JOD: 30,
                KW: 13,
                IQD: 42,
                LBP: 42,
                default: 155,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        y9jkjnvpik8v: {
          label: {
            en: 'Enhanced Visibility Package',
            ar: 'حزمة الرؤية المحسنة'
          },
          image: 'images/2365/Packages/Enhanced Visibility.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Enhanced Visibility Package includes enhanced visibility features such as a head-up display and rear view camera.',
            ar: 'تتضمن حزمة الرؤية المحسنة ميزات رؤية محسنة مثل شاشة عرض أمامية وكاميرا الرؤية الخلفية.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Head-Up Display',
                ar: 'عرض الرأس'
              },
              accessoryDescription: {
                en: 'Head-Up Display',
                ar: 'عرض الرأس'
              },
              image: 'images/2365/Packages/premier_enhanced_visibility_package_1.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              accessoryDescription: {
                en: 'Rear Camera Mirror',
                ar: 'مرآة الكاميرا الخلفية'
              },
              image: 'images/2365/Packages/premier_enhanced_visibility_package_0.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'Y74',
          brandItemCode: 'PA13',
        },
        r5wqfoofy7qd: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 471,
            UAE: 462,
            OMR: 49,
            BHD: 48,
            QAR: 457,
            JOD: 89,
            KW: 39,
            IQD: 126,
            LBP: 126,
            default: 462,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced security features along with Auto sense power and hands-free lift gate.',
            ar: 'تتضمن الحزمة التكنولوجية ميزات أمان متقدمة بالإضافة إلى نظام الاستشعار التلقائي وبوابة الرفع بدون استخدام اليدين.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Security Package',
                ar: 'حزمة الأمان المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Security Package',
                ar: 'حزمة الأمان المتقدمة'
              },
              image: 'images/2365/Packages/Technology Package.jpg',
              price: {
                KSA: 313,
                UAE: 307,
                OMR: 33,
                BHD: 32,
                QAR: 304,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 307,
              }
            },
            {
              accessoryTitle: {
                en: 'Auto Sense Power and Hands-free lift gate',
                ar: 'باب خلفي كهربائي مع استشعار تلقائي وفتح الباب بدون استخدام اليدين'
              },
              accessoryDescription: {
                en: 'Auto Sense Power and Hands-free lift gate',
                ar: 'باب خلفي كهربائي مع استشعار تلقائي وفتح الباب بدون استخدام اليدين'
              },
              image: 'images/2365/Packages/Technology Package.jpg',
              price: {
                KSA: 158,
                UAE: 155,
                OMR: 16,
                BHD: 16,
                QAR: 153,
                JOD: 30,
                KW: 13,
                IQD: 42,
                LBP: 42,
                default: 155,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        e16fx2pwr5c6: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/z71_packages.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced features such as 15inch Head-up Display and Light sensitive + Full Video Inside Rear View Mirror.',
            ar: 'تتضمن حزمة التكنولوجيا ميزات متقدمة مثل شاشة عرض أمامية مقاس 15 بوصة ومرآة الرؤية الخلفية الداخلية الحساسة للضوء + فيديو كامل'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              accessoryDescription: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              image: 'images/2365/Packages/z71_technology_package_0.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              accessoryDescription: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              image: 'images/2365/Packages/z71_technology_package_1.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        '0m9r9b96ve38': {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/z71_packages.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/z71_packages.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/z71_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/z71_packages.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/z71_comfort_package_interior.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/z71_comfort_package_interior.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/z71_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/z71_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Steering Wheel',
                ar: 'عجلة القيادة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/z71_comfort_package_interior.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        hcjds82xe95h: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced features such as 15inch Head-up Display and Light sensitive + Full Video Inside Rear View Mirror.',
            ar: 'تتضمن حزمة التكنولوجيا ميزات متقدمة مثل شاشة عرض أمامية مقاس 15 بوصة ومرآة الرؤية الخلفية الداخلية الحساسة للضوء + فيديو كامل'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              accessoryDescription: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              image: 'images/2365/Packages/rst_technology_package_0.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              accessoryDescription: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              image: 'images/2365/Packages/rst_technology_package_1.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        d0reb1sp8t84: {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5456,
            UAE: 5354,
            OMR: 567,
            BHD: 553,
            QAR: 5296,
            JOD: 1033,
            KW: 451,
            IQD: 1455,
            LBP: 1455,
            default: 5354,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 623,
                UAE: 611,
                OMR: 65,
                BHD: 63,
                QAR: 604,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 611,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 3059,
                UAE: 3002,
                OMR: 318,
                BHD: 310,
                QAR: 2969,
                JOD: 579,
                KW: 253,
                IQD: 816,
                LBP: 816,
                default: 3002,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 310,
                UAE: 304,
                OMR: 32,
                BHD: 31,
                QAR: 301,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 304,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 622,
                UAE: 610,
                OMR: 65,
                BHD: 63,
                QAR: 603,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 610,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 658,
                UAE: 646,
                OMR: 68,
                BHD: 67,
                QAR: 639,
                JOD: 125,
                KW: 54,
                IQD: 176,
                LBP: 176,
                default: 646,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 494,
                UAE: 485,
                OMR: 51,
                BHD: 50,
                QAR: 480,
                JOD: 94,
                KW: 41,
                IQD: 132,
                LBP: 132,
                default: 485,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        'd0reb1sp8t84-my26': {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5456,
            UAE: 5354,
            OMR: 567,
            BHD: 553,
            QAR: 5296,
            JOD: 1033,
            KW: 451,
            IQD: 1455,
            LBP: 1455,
            default: 5354,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 623,
                UAE: 611,
                OMR: 65,
                BHD: 63,
                QAR: 604,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 611,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 3059,
                UAE: 3002,
                OMR: 318,
                BHD: 310,
                QAR: 2969,
                JOD: 579,
                KW: 253,
                IQD: 816,
                LBP: 816,
                default: 3002,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 310,
                UAE: 304,
                OMR: 32,
                BHD: 31,
                QAR: 301,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 304,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 622,
                UAE: 610,
                OMR: 65,
                BHD: 63,
                QAR: 603,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 610,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 658,
                UAE: 646,
                OMR: 68,
                BHD: 67,
                QAR: 639,
                JOD: 125,
                KW: 54,
                IQD: 176,
                LBP: 176,
                default: 646,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/rst_comfort_package_interior.png',
              price: {
                KSA: 494,
                UAE: 485,
                OMR: 51,
                BHD: 50,
                QAR: 480,
                JOD: 94,
                KW: 41,
                IQD: 132,
                LBP: 132,
                default: 485,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        '17qszcfj0bo2': {
          label: {
            en: 'Max Trailering Package',
            ar: 'حزمة القطر القصوى'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 882,
            UAE: 866,
            OMR: 92,
            BHD: 89,
            QAR: 857,
            JOD: 167,
            KW: 73,
            IQD: 235,
            LBP: 235,
            default: 866,
          },
          packageDescription: {
            en: 'The Max Trailering Package includes a 3.42 rear axle ratio, heavy-duty rear springs, and an integrated trailer brake controller.',
            ar: 'تتضمن حزمة Max Trailering نسبة محور خلفي تبلغ 3.42، ونوابض خلفية شديدة التحمل، ووحدة تحكم في فرامل المقطورة المتكاملة.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              accessoryDescription: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                IQD: 191,
                LBP: 191,
                default: 704,
              }
            },
            {
              accessoryTitle: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              accessoryDescription: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 165,
                UAE: 162,
                OMR: 17,
                BHD: 17,
                QAR: 160,
                JOD: 31,
                KW: 14,
                IQD: 44,
                LBP: 44,
                default: 162,
              }
            }
          ],
          autoIntelRPOCode: 'NHT',
          brandItemCode: 'PA02',
        },
        k8mse77gto6i: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced features such as 15inch Head-up Display and Light sensitive + Full Video Inside Rear View Mirror.',
            ar: 'تتضمن حزمة التكنولوجيا ميزات متقدمة مثل شاشة عرض أمامية مقاس 15 بوصة ومرآة الرؤية الخلفية الداخلية الحساسة للضوء + فيديو كامل'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              accessoryDescription: {
                en: '15" Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              image: 'images/2365/Packages/rst_technology_package_0.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              accessoryDescription: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              image: 'images/2365/Packages/lt_technology_package_1.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        a5chmgxs7219: {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5456,
            UAE: 5354,
            OMR: 567,
            BHD: 553,
            QAR: 5296,
            JOD: 1033,
            KW: 451,
            IQD: 1455,
            LBP: 1455,
            default: 5354,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/lt_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 623,
                UAE: 611,
                OMR: 65,
                BHD: 63,
                QAR: 604,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 611,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 3059,
                UAE: 3002,
                OMR: 318,
                BHD: 310,
                QAR: 2969,
                JOD: 579,
                KW: 253,
                IQD: 816,
                LBP: 816,
                default: 3002,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 310,
                UAE: 304,
                OMR: 32,
                BHD: 31,
                QAR: 301,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 304,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 622,
                UAE: 610,
                OMR: 65,
                BHD: 63,
                QAR: 603,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 610,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 658,
                UAE: 646,
                OMR: 68,
                BHD: 67,
                QAR: 639,
                JOD: 125,
                KW: 54,
                IQD: 176,
                LBP: 176,
                default: 646,
              }
            },
            {
              accessoryTitle: {
                en: 'Steering Wheel',
                ar: 'عجلة القيادة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 494,
                UAE: 485,
                OMR: 51,
                BHD: 50,
                QAR: 480,
                JOD: 94,
                KW: 41,
                IQD: 132,
                LBP: 132,
                default: 485,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        kdcvdi12qfge: {
          label: {
            en: 'Black Out Package',
            ar: 'حزمة بلاك اوت'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1040,
            UAE: 1021,
            OMR: 108,
            BHD: 105,
            QAR: 1010,
            JOD: 197,
            KW: 86,
            IQD: 277,
            LBP: 277,
            default: 1021,
          },
          packageDescription: {
            en: 'The Black Out Package features Black Bowtie Emblems, Black Nameplate and Black Exhaust Tip.',
            ar: 'تتميز حزمة Black Out بشعارات Bowtie باللون الأسود ولوحة اسم باللون الأسود وطرف عادم باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              accessoryDescription: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              accessoryDescription: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              image: 'images/2365/ACC/BlackNameplatesLT.png',
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                IQD: 141,
                LBP: 141,
                default: 520,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              accessoryDescription: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              image: 'images/2365/ACC/BlackExhaustTip.png',
              price: {
                KSA: 511,
                UAE: 501,
                OMR: 53,
                BHD: 52,
                QAR: 496,
                JOD: 97,
                KW: 42,
                IQD: 136,
                LBP: 136,
                default: 501,
              }
            }
          ],
          autoIntelRPOCode: 'RGF',
          brandItemCode: 'PA16',
        },
        q1rjmnbq9vgu: {
          label: {
            en: 'Illumination Package',
            ar: 'حزمة الإضاءة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Luxury Package features Front LED Fog Lamps and Auto Dimming Inside Rear View Mirror.',
            ar: 'تتميز الحزمة الفاخرة بمصابيح ضباب أمامية LED ومرآة الرؤية الخلفية الداخلية ذات التعتيم التلقائي'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Illuminated Black Bowtie',
                ar: 'ربطة عنق سوداء مضيئة'
              },
              accessoryDescription: {
                en: 'Illuminated Black Bowtie',
                ar: 'ربطة عنق سوداء مضيئة'
              },
              image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Illuminted Sill Plates ',
                ar: 'لوحات عتبات مضيئة'
              },
              accessoryDescription: {
                en: 'Illuminted Sill Plates ',
                ar: 'لوحات عتبات مضيئة'
              },
              image: 'images/2365/ACC/IlluminatedSillPlates.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Puddle Light',
                ar: 'ضوء البركة'
              },
              accessoryDescription: {
                en: 'Puddle Light',
                ar: 'ضوء البركة'
              },
              image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          autoIntelRPOCode: 'RGL',
          brandItemCode: 'PA17',
        },
        '7jbrmnpu41z1': {
          label: {
            en: 'Luxury Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1564,
            UAE: 1535,
            OMR: 163,
            BHD: 159,
            QAR: 1518,
            JOD: 296,
            KW: 129,
            IQD: 417,
            LBP: 417,
            default: 1535,
          },
          packageDescription: {
            en: 'The Luxury Package includes LED Fog Lamps with Auto Dimming Inside Rear View Mirror.',
            ar: 'تتضمن الحزمة الفاخرة مصابيح ضباب LED مع خاصية التعتيم التلقائي في مرآة الرؤية الخلفية الداخلية.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Fog Lamps',
                ar: 'مصابيح الضباب LED'
              },
              accessoryDescription: {
                en: 'Front LED Fog Lamps',
                ar: 'مصابيح الضباب الأمامية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية'
              },
              accessoryDescription: {
                en: 'Auto Dimming Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية ذاتية التعتيم'
              },
              image: 'images/2365/Packages/lt_luxury_package_inside.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'WPL',
          brandItemCode: 'PA01',
        },
        y2dzpqaviuzr: {
          label: {
            en: 'Advanced Trailering Package',
            ar: 'باقة القطر المتقدمة'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 461,
            UAE: 452,
            OMR: 48,
            BHD: 47,
            QAR: 447,
            JOD: 87,
            KW: 38,
            IQD: 123,
            LBP: 123,
            default: 452,
          },
          packageDescription: {
            en: 'The Advanced Trailering Package enhance and monitor the towing experience.',
            ar: 'تعمل حزمة القطر المتقدمة على تحسين ومراقبة تجربة القطر.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                IQD: 123,
                LBP: 123,
                default: 452,
              }
            }
          ],
          autoIntelRPOCode: 'ZL6',
          brandItemCode: 'PA19',
        },
        y2dzpqaviuz2: {
          label: {
            en: 'Max Trailering Package',
            ar: 'حزمة القطر القصوى'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 882,
            UAE: 866,
            OMR: 92,
            BHD: 89,
            QAR: 857,
            JOD: 167,
            KW: 73,
            IQD: 235,
            LBP: 235,
            default: 866,
          },
          packageDescription: {
            en: 'The Max Trailering Package includes a 3.42 rear axle ratio, heavy-duty rear springs, and an integrated trailer brake controller.',
            ar: 'تتضمن حزمة Max Trailering نسبة محور خلفي تبلغ 3.42، ونوابض خلفية شديدة التحمل، ووحدة تحكم في فرامل المقطورة المتكاملة.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              accessoryDescription: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                IQD: 191,
                LBP: 191,
                default: 704,
              }
            },
            {
              accessoryTitle: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              accessoryDescription: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 165,
                UAE: 162,
                OMR: 17,
                BHD: 17,
                QAR: 160,
                JOD: 31,
                KW: 14,
                IQD: 44,
                LBP: 44,
                default: 162,
              }
            }
          ],
          autoIntelRPOCode: 'NHT',
          brandItemCode: 'PA02',
        },
        tfdp0ldjts3m: {
          label: {
            en: 'Technology Package',
            ar: 'حزمة التكنولوجيا'
          },
          image: 'images/2365/Packages/Technology Package.jpg',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 4198,
            UAE: 4120,
            OMR: 437,
            BHD: 425,
            QAR: 4075,
            JOD: 795,
            KW: 347,
            IQD: 1120,
            LBP: 1120,
            default: 4120,
          },
          packageDescription: {
            en: 'The Technology Package includes advanced features such as 15inch Head-up Display and Light sensitive + Full Video Inside Rear View Mirror.',
            ar: 'تتضمن حزمة التكنولوجيا ميزات متقدمة مثل شاشة عرض أمامية مقاس 15 بوصة ومرآة الرؤية الخلفية الداخلية الحساسة للضوء + فيديو كامل'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: '15inch Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              accessoryDescription: {
                en: '15inch Head-Up Display',
                ar: 'شاشة أمامية مقاس 15 بوصة'
              },
              image: 'images/2365/Packages/rst_technology_package_0.png',
              price: {
                KSA: 2634,
                UAE: 2585,
                OMR: 274,
                BHD: 267,
                QAR: 2557,
                JOD: 499,
                KW: 218,
                IQD: 702,
                LBP: 702,
                default: 2585,
              }
            },
            {
              accessoryTitle: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              accessoryDescription: {
                en: 'Light Sensitive + Full Video Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية الحساسة للضوء + فيديو كامل داخل المرآة'
              },
              image: 'images/2365/Packages/lt_technology_package_1.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'CWM',
          brandItemCode: 'PA03',
        },
        nlkpwwp04yae: {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5456,
            UAE: 5354,
            OMR: 567,
            BHD: 553,
            QAR: 5296,
            JOD: 1033,
            KW: 451,
            IQD: 1455,
            LBP: 1455,
            default: 5354,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/lt_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 623,
                UAE: 611,
                OMR: 65,
                BHD: 63,
                QAR: 604,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 611,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 3059,
                UAE: 3002,
                OMR: 318,
                BHD: 310,
                QAR: 2969,
                JOD: 579,
                KW: 253,
                IQD: 816,
                LBP: 816,
                default: 3002,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 310,
                UAE: 304,
                OMR: 32,
                BHD: 31,
                QAR: 301,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 304,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 622,
                UAE: 610,
                OMR: 65,
                BHD: 63,
                QAR: 603,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 610,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 658,
                UAE: 646,
                OMR: 68,
                BHD: 67,
                QAR: 639,
                JOD: 125,
                KW: 54,
                IQD: 176,
                LBP: 176,
                default: 646,
              }
            },
            {
              accessoryTitle: {
                en: 'Steering Wheel',
                ar: 'عجلة القيادة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 494,
                UAE: 485,
                OMR: 51,
                BHD: 50,
                QAR: 480,
                JOD: 94,
                KW: 41,
                IQD: 132,
                LBP: 132,
                default: 485,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        'nlkpwwp04yae-my26': {
          label: {
            en: 'Comfort Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 5456,
            UAE: 5354,
            OMR: 567,
            BHD: 553,
            QAR: 5296,
            JOD: 1033,
            KW: 451,
            IQD: 1455,
            LBP: 1455,
            default: 5354,
          },
          packageDescription: {
            en: 'The Comfort Package includes features designed to enhance passenger comfort, such as heated seats, LED animated Headlamps and Taillamps, Power Folding Side View Mirrors and speakers',
            ar: 'تتضمن حزمة الراحة ميزات مصممة لتعزيز راحة الركاب، مثل المقاعد المدفأة والمصابيح الأمامية المتحركة LED والمصابيح الخلفية والمرايا الجانبية القابلة للطي كهربائيًا ومكبرات الصوت'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Headlamp',
                ar: 'كشافات LED'
              },
              accessoryDescription: {
                en: 'LED Animated Headlamp',
                ar: 'مصباح أمامي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'LED Taillamp',
                ar: 'مصباح خلفي LED'
              },
              accessoryDescription: {
                en: 'LED Animated Taillamp',
                ar: 'مصباح خلفي متحرك بتقنية LED'
              },
              image: 'images/2365/Packages/lt_comfort_package_taillamp.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Side View Mirror',
                ar: 'مرآة الرؤية الجانبية'
              },
              accessoryDescription: {
                en: 'Power Folding Side View Mirror',
                ar: 'مرآة الرؤية الجانبية القابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 623,
                UAE: 611,
                OMR: 65,
                BHD: 63,
                QAR: 604,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 611,
              }
            },
            {
              accessoryTitle: {
                en: 'Bose - 10 Speaker',
                ar: 'بوس - 10 سماعات'
              },
              accessoryDescription: {
                en: 'Bose-10 Speakers Souround with CentrePoint',
                ar: 'مكبرات صوت Bose-10 Souround مع CentrePoint'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 3059,
                UAE: 3002,
                OMR: 318,
                BHD: 310,
                QAR: 2969,
                JOD: 579,
                KW: 253,
                IQD: 816,
                LBP: 816,
                default: 3002,
              }
            },
            {
              accessoryTitle: {
                en: 'Memory Position',
                ar: 'موضع الذاكرة'
              },
              accessoryDescription: {
                en: 'Memory Driver Seat, Steering and Side Mirror Position',
                ar: 'ذاكرة مقعد السائق، وعجلة القيادة، وموضع المرآة الجانبية'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 310,
                UAE: 304,
                OMR: 32,
                BHD: 31,
                QAR: 301,
                JOD: 59,
                KW: 26,
                IQD: 83,
                LBP: 83,
                default: 304,
              }
            },
            {
              accessoryTitle: {
                en: 'Folding Seats',
                ar: 'مقاعد قابلة للطي'
              },
              accessoryDescription: {
                en: 'Power Folding 2nd and 3rd Row Seats',
                ar: 'مقاعد الصف الثاني والثالث قابلة للطي كهربائيًا'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 622,
                UAE: 610,
                OMR: 65,
                BHD: 63,
                QAR: 603,
                JOD: 118,
                KW: 51,
                IQD: 166,
                LBP: 166,
                default: 610,
              }
            },
            {
              accessoryTitle: {
                en: 'Heated Seats',
                ar: 'مقاعد مدفأة'
              },
              accessoryDescription: {
                en: 'Heated 2nd Row Seats',
                ar: 'مقاعد الصف الثاني المُدفأة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior_mid_seat.png',
              price: {
                KSA: 658,
                UAE: 646,
                OMR: 68,
                BHD: 67,
                QAR: 639,
                JOD: 125,
                KW: 54,
                IQD: 176,
                LBP: 176,
                default: 646,
              }
            },
            {
              accessoryTitle: {
                en: 'Steering Wheel',
                ar: 'عجلة القيادة'
              },
              accessoryDescription: {
                en: 'Heated Steering Wheel',
                ar: 'عجلة القيادة ساخنة'
              },
              image: 'images/2365/Packages/lt_comfort_package_interior.png',
              price: {
                KSA: 494,
                UAE: 485,
                OMR: 51,
                BHD: 50,
                QAR: 480,
                JOD: 94,
                KW: 41,
                IQD: 132,
                LBP: 132,
                default: 485,
              }
            }
          ],
          autoIntelRPOCode: 'Y55',
          brandItemCode: 'PA04',
        },
        wxq5g7rzvivx: {
          label: {
            en: 'Black Out Package',
            ar: 'حزمة بلاك اوت'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1040,
            UAE: 1021,
            OMR: 108,
            BHD: 105,
            QAR: 1010,
            JOD: 197,
            KW: 86,
            IQD: 277,
            LBP: 277,
            default: 1021,
          },
          packageDescription: {
            en: 'The Black Out Package features Black Bowtie Emblems, Black Nameplate and Black Exhaust Tip.',
            ar: 'تتميز حزمة Black Out بشعارات Bowtie باللون الأسود ولوحة اسم باللون الأسود وطرف عادم باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              accessoryDescription: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              accessoryDescription: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              image: 'images/2365/ACC/BlackNameplatesLT.png',
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                IQD: 141,
                LBP: 141,
                default: 520,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              accessoryDescription: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              image: 'images/2365/ACC/BlackExhaustTip.png',
              price: {
                KSA: 511,
                UAE: 501,
                OMR: 53,
                BHD: 52,
                QAR: 496,
                JOD: 97,
                KW: 42,
                IQD: 136,
                LBP: 136,
                default: 501,
              }
            }
          ],
          autoIntelRPOCode: 'RGF',
          brandItemCode: 'PA16',
        },
        ysk7er2cyj1ffds: {
          label: {
            en: 'Illumination Package',
            ar: 'حزمة الإضاءة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 0,
            UAE: 0,
            OMR: 0,
            BHD: 0,
            QAR: 0,
            JOD: 0,
            default: 0,
          },
          packageDescription: {
            en: 'The Luxury Package features Front LED Fog Lamps and Auto Dimming Inside Rear View Mirror.',
            ar: 'تتميز الحزمة الفاخرة بمصابيح ضباب أمامية LED ومرآة الرؤية الخلفية الداخلية ذات التعتيم التلقائي'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Illuminated Black Bowtie',
                ar: 'ربطة عنق سوداء مضيئة'
              },
              accessoryDescription: {
                en: 'Illuminated Black Bowtie',
                ar: 'ربطة عنق سوداء مضيئة'
              },
              image: 'images/2365/ACC/ACC_IlluminatedBlackBowtie.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Illuminted Sill Plates ',
                ar: 'لوحات عتبات مضيئة'
              },
              accessoryDescription: {
                en: 'Illuminted Sill Plates ',
                ar: 'لوحات عتبات مضيئة'
              },
              image: 'images/2365/ACC/IlluminatedSillPlates.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Puddle Light',
                ar: 'ضوء البركة'
              },
              accessoryDescription: {
                en: 'Puddle Light',
                ar: 'ضوء البركة'
              },
              image: 'images/2365/ACC/ACC_BowtieLogoPuddleLights.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                default: 0,
              }
            }
          ],
          autoIntelRPOCode: 'RGL',
          brandItemCode: 'PA17',
        },
        '7jbrmnpu41zt': {
          label: {
            en: 'Luxury Package',
            ar: 'باقة الراحة'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1564,
            UAE: 1535,
            OMR: 163,
            BHD: 159,
            QAR: 1518,
            JOD: 296,
            KW: 129,
            IQD: 417,
            LBP: 417,
            default: 1535,
          },
          packageDescription: {
            en: 'The Luxury Package includes LED Fog Lamps with Auto Dimming Inside Rear View Mirror.',
            ar: 'تتضمن الحزمة الفاخرة مصابيح ضباب LED مع خاصية التعتيم التلقائي في مرآة الرؤية الخلفية الداخلية.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'LED Fog Lamps',
                ar: 'مصابيح الضباب LED'
              },
              accessoryDescription: {
                en: 'Front LED Fog Lamps',
                ar: 'مصابيح الضباب الأمامية LED'
              },
              image: 'images/2365/Packages/rst_comfort_package.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية'
              },
              accessoryDescription: {
                en: 'Auto Dimming Inside Rear View Mirror',
                ar: 'مرآة الرؤية الخلفية ذاتية التعتيم'
              },
              image: 'images/2365/Packages/lt_luxury_package_inside.png',
              price: {
                KSA: 1564,
                UAE: 1535,
                OMR: 163,
                BHD: 159,
                QAR: 1518,
                JOD: 296,
                KW: 129,
                IQD: 417,
                LBP: 417,
                default: 1535,
              }
            }
          ],
          autoIntelRPOCode: 'WPL',
          brandItemCode: 'PA01',
        },
        y2dzpqaviuz1: {
          label: {
            en: 'Advanced Trailering Package',
            ar: 'باقة القطر المتقدمة'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 461,
            UAE: 452,
            OMR: 48,
            BHD: 47,
            QAR: 447,
            JOD: 87,
            KW: 38,
            IQD: 123,
            LBP: 123,
            default: 452,
          },
          packageDescription: {
            en: 'The Advanced Trailering Package enhance and monitor the towing experience.',
            ar: 'تعمل حزمة القطر المتقدمة على تحسين ومراقبة تجربة القطر.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                IQD: 123,
                LBP: 123,
                default: 452,
              }
            }
          ],
          autoIntelRPOCode: 'ZL6',
          brandItemCode: 'PA19',
        },
        tupambf5esb8: {
          label: {
            en: 'Max Trailering Package',
            ar: 'حزمة القطر القصوى'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 882,
            UAE: 866,
            OMR: 92,
            BHD: 89,
            QAR: 857,
            JOD: 167,
            KW: 73,
            IQD: 235,
            LBP: 235,
            default: 866,
          },
          packageDescription: {
            en: 'The Max Trailering Package includes a 3.42 rear axle ratio, heavy-duty rear springs, and an integrated trailer brake controller.',
            ar: 'تتضمن حزمة Max Trailering نسبة محور خلفي تبلغ 3.42، ونوابض خلفية شديدة التحمل، ووحدة تحكم في فرامل المقطورة المتكاملة.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              accessoryDescription: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                IQD: 191,
                LBP: 191,
                default: 704,
              }
            },
            {
              accessoryTitle: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              accessoryDescription: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 165,
                UAE: 162,
                OMR: 17,
                BHD: 17,
                QAR: 160,
                JOD: 31,
                KW: 14,
                IQD: 44,
                LBP: 44,
                default: 162,
              }
            }
          ],
          autoIntelRPOCode: 'NHT',
          brandItemCode: 'PA02',
        },
        y2dzpqaviuz3: {
          label: {
            en: 'Advanced Trailering Package',
            ar: 'باقة القطر المتقدمة'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 461,
            UAE: 452,
            OMR: 48,
            BHD: 47,
            QAR: 447,
            JOD: 87,
            KW: 38,
            IQD: 123,
            LBP: 123,
            default: 452,
          },
          packageDescription: {
            en: 'The Advanced Trailering Package enhance and monitor the towing experience.',
            ar: 'تعمل حزمة القطر المتقدمة على تحسين ومراقبة تجربة القطر.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                IQD: 123,
                LBP: 123,
                default: 452,
              }
            }
          ],
          autoIntelRPOCode: 'ZL6',
          brandItemCode: 'PA19',
        },
        wcjcjdf6vud7: {
          label: {
            en: 'Black Out Package',
            ar: 'حزمة بلاك اوت'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1040,
            UAE: 1021,
            OMR: 108,
            BHD: 105,
            QAR: 1010,
            JOD: 197,
            KW: 86,
            IQD: 277,
            LBP: 277,
            default: 1021,
          },
          packageDescription: {
            en: 'The Black Out Package features Black Bowtie Emblems, Black Nameplate and Black Exhaust Tip.',
            ar: 'تتميز حزمة Black Out بشعارات Bowtie باللون الأسود ولوحة اسم باللون الأسود وطرف عادم باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              accessoryDescription: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              accessoryDescription: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              image: 'images/2365/ACC/BlackNameplatesLT.png',
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                IQD: 141,
                LBP: 141,
                default: 520,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              accessoryDescription: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              image: 'images/2365/ACC/BlackExhaustTip.png',
              price: {
                KSA: 511,
                UAE: 501,
                OMR: 53,
                BHD: 52,
                QAR: 496,
                JOD: 97,
                KW: 42,
                IQD: 136,
                LBP: 136,
                default: 501,
              }
            }
          ],
          autoIntelRPOCode: 'RGF',
          brandItemCode: 'PA16',
        },
        '4e8rsvm8yulq0': {
          label: {
            en: 'Max Trailering Package',
            ar: 'حزمة القطر القصوى'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 882,
            UAE: 866,
            OMR: 92,
            BHD: 89,
            QAR: 857,
            JOD: 167,
            KW: 73,
            IQD: 235,
            LBP: 235,
            default: 866,
          },
          packageDescription: {
            en: 'The Max Trailering Package includes a 3.42 rear axle ratio, heavy-duty rear springs, and an integrated trailer brake controller.',
            ar: 'تتضمن حزمة Max Trailering نسبة محور خلفي تبلغ 3.42، ونوابض خلفية شديدة التحمل، ووحدة تحكم في فرامل المقطورة المتكاملة.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              accessoryDescription: {
                en: 'Trailer Brake Controller',
                ar: 'جهاز التحكم بفرامل المقطورة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 717,
                UAE: 704,
                OMR: 75,
                BHD: 73,
                QAR: 696,
                JOD: 136,
                KW: 59,
                IQD: 191,
                LBP: 191,
                default: 704,
              }
            },
            {
              accessoryTitle: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              accessoryDescription: {
                en: 'Extra Capacity Cooling System',
                ar: 'نظام تبريد ذو سعة إضافية'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 165,
                UAE: 162,
                OMR: 17,
                BHD: 17,
                QAR: 160,
                JOD: 31,
                KW: 14,
                IQD: 44,
                LBP: 44,
                default: 162,
              }
            }
          ],
          autoIntelRPOCode: 'NHT',
          brandItemCode: 'PA02',
        },
        ysk7er2cyj1f34: {
          label: {
            en: 'Black Out Package',
            ar: 'حزمة بلاك اوت'
          },
          image: 'images/2365/Packages/rst_comfort_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 1040,
            UAE: 1021,
            OMR: 108,
            BHD: 105,
            QAR: 1010,
            JOD: 197,
            KW: 86,
            IQD: 277,
            LBP: 277,
            default: 1021,
          },
          packageDescription: {
            en: 'The Black Out Package features Black Bowtie Emblems, Black Nameplate and Black Exhaust Tip.',
            ar: 'تتميز حزمة Black Out بشعارات Bowtie باللون الأسود ولوحة اسم باللون الأسود وطرف عادم باللون الأسود.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              accessoryDescription: {
                en: 'Black Bowtie Emblems',
                ar: 'شعارات ربطة العنق السوداء'
              },
              image: 'images/2365/ACC/ACC_BlackBowtieEmblems.png',
              price: {
                KSA: 0,
                UAE: 0,
                OMR: 0,
                BHD: 0,
                QAR: 0,
                JOD: 0,
                KW: 0,
                default: 0,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              accessoryDescription: {
                en: 'Black Nameplates',
                ar: 'لوحات تحمل أسماء سوداء'
              },
              image: 'images/2365/ACC/BlackNameplatesLT.png',
              price: {
                KSA: 530,
                UAE: 520,
                OMR: 55,
                BHD: 54,
                QAR: 514,
                JOD: 100,
                KW: 44,
                IQD: 141,
                LBP: 141,
                default: 520,
              }
            },
            {
              accessoryTitle: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              accessoryDescription: {
                en: 'Black Exhaust Tip',
                ar: 'طرف عادم أسود'
              },
              image: 'images/2365/ACC/BlackExhaustTip.png',
              price: {
                KSA: 511,
                UAE: 501,
                OMR: 53,
                BHD: 52,
                QAR: 496,
                JOD: 97,
                KW: 42,
                IQD: 136,
                LBP: 136,
                default: 501,
              }
            }
          ],
          autoIntelRPOCode: 'RGF',
          brandItemCode: 'PA16',
        },
        y2dzpqaviuz4: {
          label: {
            en: 'Advanced Trailering Package',
            ar: 'باقة القطر المتقدمة'
          },
          image: 'images/2365/RST/max_trailering_package.png',
          category: {
            en: 'Packages',
            ar: 'الحزم'
          },
          price: {
            KSA: 461,
            UAE: 452,
            OMR: 48,
            BHD: 47,
            QAR: 447,
            JOD: 87,
            KW: 38,
            IQD: 123,
            LBP: 123,
            default: 452,
          },
          packageDescription: {
            en: 'The Advanced Trailering Package enhance and monitor the towing experience.',
            ar: 'تعمل حزمة القطر المتقدمة على تحسين ومراقبة تجربة القطر.'
          },
          cardType: 'cart',
          hasQuantity: false,
          accessoryContent: [
            {
              accessoryTitle: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              accessoryDescription: {
                en: 'Advanced Trailering Package',
                ar: 'باقة القطر المتقدمة'
              },
              image: 'images/2365/RST/max_trailering_package.png',
              price: {
                KSA: 461,
                UAE: 452,
                OMR: 48,
                BHD: 47,
                QAR: 447,
                JOD: 87,
                KW: 38,
                IQD: 123,
                LBP: 123,
                default: 452,
              }
            }
          ],
          autoIntelRPOCode: 'ZL6',
          brandItemCode: 'PA19',
        }
      },
      interactions: {
        xbdzchklx04d: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '9a4ewd0iqpwk': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        fwou2afhghpp: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        bkr8amy2tvbs: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '6146uzbd6kui': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        iq7p37hq4g0j: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        z1338jyjvioh: {
          label: {
            en: 'Fog Lamps',
            ar: 'مصابيح الضباب'
          },
          type: 'interaction'
        },
        whla8blk6rfp: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '9sdtnnxpfmab': {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        a9037m7w0amd: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '9vjnbyvcm8ye': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        xvlpl9hn6ljm: {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        fih0thha3470: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '44otdgfdocii': {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        '2oa1nn1eq5ws': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        tzk3p1e9inza: {
          label: {
            en: 'Fog Lamps',
            ar: 'مصابيح الضباب'
          },
          type: 'interaction'
        },
        petliz6yx4hn: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        b47gdog8ibnv: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '0vth6kpxs1jg': {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        kisy9chslzdy: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '73ziza3xg8ou': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        '4xi4tptsom06': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        ak1tl37gumgs: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        v713uc2xqr67: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        isdzpznxq6g8: {
          label: {
            en: 'Fog Lamps',
            ar: 'مصابيح الضباب'
          },
          type: 'interaction'
        },
        yplmpl5hbfjn: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        v7ns4gk7q7x4: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        dscofhz7g4yx: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        xy419msn5u11: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '55mqnedm4ie5': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        sqb2g38cd9f6: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        vwruq487gini: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        lgzk0rcg6nra: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        '66sikckzjmb5': {
          label: {
            en: 'Fog Lamps',
            ar: 'مصابيح الضباب'
          },
          type: 'interaction'
        },
        ga1j8nalmcub: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        n2nwscexca19: {
          label: {
            en: 'Suspension',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        nr092e0zwxmq: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        n3srfdzi1l8y: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '349fglfh95t5': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        '0spridh8lbtj': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        iatxs9qe275v: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        ohkbhiy361mh: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        xrckn309uytv: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        hwol9wjk8u5f: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        a0b4x8u9auhj: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '2mbv5mgd2j0t': {
          label: {
            en: 'Sunroof',
            ar: 'فتحة السقف'
          },
          type: 'interaction'
        },
        qt4x1krtddkb: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        z93m8aegiqzi: {
          label: {
            en: 'DRL',
            ar: 'DRL'
          },
          type: 'interaction'
        },
        bq3rqckxu99j: {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        szn7n8dgwxam: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        glpt65jo78y2: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        t6e4q7plmhff: {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        ksc6tjza73uc: {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '9tzgl55xbnhv': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        r7u7zjrxkryo: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        f9s7b4hdeiz7: {
          label: {
            en: 'Doors',
            ar: 'الأبواب'
          },
          type: 'interaction'
        },
        '1nad76ictp6j': {
          label: {
            en: 'Boot',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        },
        '3cqy8q29b96c': {
          label: {
            en: 'Headlight',
            ar: 'ضوء السقف'
          },
          type: 'interaction'
        },
        '1dzdmvhl8vp3': {
          label: {
            en: 'Taillight',
            ar: 'الضوء الخلفي'
          },
          type: 'interaction'
        },
        vb0nbeis8kuj: {
          label: {
            en: '3nd Row Folding Seat',
            ar: 'صندوق المركبة'
          },
          type: 'interaction'
        }
      },
      hotspots: {
        exteriorCamera: {
          title: {
            en: 'Exterior',
            ar: 'الخارج'
          }
        },
        interiorCamera: {
          title: {
            en: 'First Row Seat',
            ar: 'مقعد الصف الأول'
          }
        },
        interiorCamera_1: {
          title: {
            en: 'Second Row Seat',
            ar: 'مقعد الصف الثاني'
          }
        },
        interiorCamera_2: {
          title: {
            en: 'Third Row Seat',
            ar: 'مقعد الصف الثالث'
          }
        }
      },
      name: {
        en: 'Chevrolet Tahoe',
        ar: 'Chevrolet Tahoe'
      },
      specifications: {
        heading: {
          en: 'Chevrolet Tahoe',
          ar: 'Chevrolet Tahoe'
        },
        trim: {
          en: '',
          ar: ''
        },
        price: {
          en: '',
          ar: ''
        },
        model: 'Chevrolet Tahoe',
        seatingCapacity: '8/9',
        dimensions: {
          length: '5352 mm',
          width: '',
          height: '',
          wheelbase: '3071 mm',
          groundClearance: ''
        },
        performance: [
          {
            value: '355/420',
            unit: 'HP',
            label: {
              en: '',
              ar: ''
            }
          },
          {
            value: '518/624',
            unit: 'NM',
            label: {
              en: 'Torque',
              ar: 'عزم الدوران'
            }
          },
          {
            value: '5.3L/6.2L',
            unit: '',
            label: {
              en: 'V8 Engine',
              ar: 'V8 محرك'
            }
          }
        ],
        summary: [
          {
            title: {
              en: 'Model',
              ar: 'نموذج'
            },
            subtitle: {
              en: 'Chevrolet Tahoe',
              ar: 'Chevrolet Tahoe'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Exterior Colors',
              ar: 'الألوان الخارجية'
            },
            subtitle: {
              en: 'Iridescent Pearl Tricoat, Dark Ash Metallic, Summit White, Black, Cypress Gray, Radiant Red Tintcoat, Sterling Gray Metallic, Lakeshore Blue Metallic (As per selected trim)',
              ar: 'ثلاثة خيارات للألوان: لؤلؤي، رمادي غامق معدني، أبيض قمة، أسود، رمادي سرو، أحمر مشع، رمادي ستيرلينج معدني، أزرق ليكشور معدني (حسب طراز السيارة المحدد)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Interior',
              ar: 'الداخلية'
            },
            subtitle: {
              en: 'Gideon / Very Dark Atmosphere, Jet Black / Victory Red, Jet Black / Sky Cool Gray (As per selected trims)',
              ar: 'جيديون / جو داكن للغاية، أسود نفاث / أحمر النصر، أسود نفاث / رمادي سكاي كول (حسب التشطيبات المختارة)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Wheelbase',
              ar: 'قاعدة العجلات'
            },
            subtitle: {
              en: '3071 mm',
              ar: '3071 mm'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Length',
              ar: 'Length'
            },
            subtitle: {
              en: '5352 mm',
              ar: '5352 mm'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Cargo Space',
              ar: 'Cargo Space'
            },
            subtitle: {
              en: 'Behind 1st row seat- 3474.9 litres, Behind 2nd row seat- 2053.2 litres, Behind 3rd row seat- 722.2 liters',
              ar: 'خلف مقعد الصف الأول - 3474.9 لترًا، خلف مقعد الصف الثاني - 2053.2 لترًا، خلف مقعد الصف الثالث - 722.2 لترًا'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Powertrain',
              ar: 'Powertrain'
            },
            subtitle: {
              en: 'Rear Wheel Drive / Four Wheel Drive (As per selected trims)',
              ar: 'دفع خلفي / دفع رباعي (حسب الطرازات المختارة)'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Transmission',
              ar: 'الانتقال'
            },
            subtitle: {
              en: '10-speed Automatic',
              ar: 'ناقل حركة أوتوماتيكي بـ 10 سرعات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'OnStar Connect Plan',
              ar: 'خطة OnStar Connect'
            },
            subtitle: {
              en: '3 Years',
              ar: '3 سنوات'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Seating Capacity',
              ar: 'سعة الجلوس'
            },
            subtitle: {
              en: '8/9',
              ar: '8/9'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          },
          {
            title: {
              en: 'Infotainment Screen',
              ar: 'شاشة المعلومات والترفيه'
            },
            subtitle: {
              en: '17.7 diagonal advanced color LCD display with Google built-in compatibility',
              ar: 'شاشة LCD ملونة متطورة مقاس 17.7 بوصة مع توافق مدمج مع Google'
            },
            price: {
              KSA: 0,
              UAE: 0,
              OMR: 0,
              BHD: 0,
              QAR: 0,
              JOD: 0,
              default: 0,
            }
          }
        ]
      },
      accessoryCategories: {
        exterior: [
          'Roof',
          'Alloy Wheels'
        ],
        interior: [
          'Seats'
        ],
        options: [
          'Options & Accessories'
        ]
      }
    }
  },
  plugins: {
    deferredAssetLoading: true,
  },
  staticTranslations: {
    specDetails: {
      model: {
        en: 'Model',
        ar: 'نموذج',
      },
      exteriorColours: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interiorAmbience: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      seatingCapacity: {
        en: 'Seating Capacity',
        ar: 'سعة المقاعد',
      },
      length: {
        en: 'Length',
        ar: 'الطول',
      },
      width: {
        en: 'Width',
        ar: 'العرض',
      },
      height: {
        en: 'Height',
        ar: 'الارتفاع',
      },
      wheelbase: {
        en: 'Wheelbase',
        ar: 'قاعدة العجلات',
      },
      groundClearance: {
        en: 'Ground Clearance',
        ar: 'الخلوص الأرضي',
      },
      share: {
        en: 'Share',
        ar: 'يشارك',
      },
    },
    variantSelection: {
      title: {
        en: 'Model',
        ar: 'نموذج',
      },
      select: {
        en: 'Select',
        ar: 'تحديد',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      selectBodyStyle: { en: 'Select Body Style', ar: 'اختر نمط الهيكل' },
      availableTrims: { en: 'Select Trim', ar: 'اختر الفئة' },
      driveType: { en: 'Drive Type', ar: 'نوع القيادة' },
      model: { en: 'Model', ar: 'الموديل' },
    },
    loadingScreen: {
      default: {
        en: 'Preparing the canvas for your dream car. Just a few more seconds to go!',
        ar: 'لحظات ونبدأ رحلة تصميم سيارة أحلامك... باقي ثوانٍ قليلة!',
      },
      enterConfigurator: {
        en: 'Build Your Ride',
        ar: 'ابنِ سيارتك',
      },
      loading: { en: 'Loading', ar: 'جارٍ التحميل' },
      variantLoading: { en: 'Trim is loading', ar: 'نموذج جارٍ التحميل' },
      viewLoading: {
        en: 'Applying final touches',
        ar: 'تطبيق اللمسات النهائية',
      },
      disclaimer: {
        en: 'Disclaimer',
        ar: 'تنصل',
      },
      termsConditions: {
        en: 'Terms & Conditions',
        ar: 'الشروط والأحكام',
      },
      viewSpecDetails: {
        en: 'View Spec Details',
        ar: 'ععرض تفاصيل المواصفات',
      },
      disclaimerContent: {
        en: 'Prices are subject to change without prior notice and may vary based on location, availability, or other factors.',
        ar: 'الأسعار عرضة للتغيير دون إشعار مسبق وقد تختلف بناءً على الموقع والتوافر وعوامل أخرى.',
      },
      termsModal: {
        title: {
          en: 'Configurator Use Terms and Conditions',
          ar: 'شروط وأحكام استخدام المُكوِّن',
        },
        lead: {
          en: 'By using this vehicle configurator and submitting your build, you acknowledge and agree to the following:',
          ar: 'من خلال استخدام مُكوِّن تكوين السيارة هذا وإرسال تصميمك، فإنك تقر وتوافق على ما يلي:',
        },
        sections: {
          item1: {
            title: {
              en: 'Non-Binding Submission',
              ar: 'تقديم غير ملزم',
            },
            content: {
              en: 'Submitting your vehicle configuration does not constitute a formal vehicle order or purchase agreement. No payment or deposit is collected at this stage.',
              ar: 'إرسال مواصفات سيارتك لا يُعد طلبًا رسميًا للسيارة أو اتفاقية شراء. لا يتم تحصيل أي مبلغ أو دفعة مقدمة في هذه المرحلة.',
            },
          },
          item2: {
            title: {
              en: 'Preliminary Estimate',
              ar: 'تقدير أولي',
            },
            content: {
              en: 'All pricing displayed is Manufacturer’s Suggested Retail Price (MSRP)and is provided for informational purposes only. Final pricing, taxes, fees, and applicable offers will be confirmed by the authorized dealer.',
              ar: 'جميع الأسعار المعروضة هي سعر التجزئة المقترح من الشركة المصنعة، وهي لأغراض إعلامية فقط. سيتم تأكيد الأسعار النهائية والضرائب والرسوم والعروض المطبقة من قبل الوكيل المعتمد.',
            },
          },
          item3: {
            title: {
              en: 'Dealer Follow-Up',
              ar: 'متابعة التاجر',
            },
            content: {
              en: 'Your submitted configuration will be shared with a participating dealer, who will contact you to discuss your preferences, availability, and next steps.',
              ar: 'سيتم مشاركة التكوين الذي أرسلته مع أحد التجار المشاركين، والذي سيتصل بك لمناقشة تفضيلاتك وتوافرك والخطوات التالية.',
            },
          },
          item4: {
            title: {
              en: 'Availability and Fulfillment',
              ar: 'التوفر والوفاء',
            },
            content: {
              en: 'We will make every reasonable effort to fulfill your preferences; however, final vehicle and accessories selection is subject to dealer inventory and production availability. Configurations may be limited to in-stock or incoming vehicles.',
              ar: 'سنبذل قصارى جهدنا لتلبية تفضيلاتكم، إلا أن الاختيار النهائي للمركبات والملحقات يخضع لمخزون الوكيل وتوافر الإنتاج. قد تقتصر التكوينات على المركبات المتوفرة في المخزون أو الواردة.',
            },
          },
          item5: {
            title: {
              en: 'No Obligation',
              ar: 'لا يوجد التزام',
            },
            content: {
              en: 'You are under no obligation to purchase the configured vehicle, and submitting your configuration does not create a contractual relationship between you and the manufacturer or dealer.',
              ar: 'لا يوجد أي التزام عليك بشراء السيارة المخصصة، كما أن إرسال التكوين الخاص بك لا يؤدي إلى إنشاء علاقة تعاقدية بينك وبين الشركة المصنعة أو التاجر.',
            },
          },
          item6: {
            title: {
              en: 'Use of Personal Information and Consent',
              ar: 'استخدام المعلومات الشخصية والموافقة',
            },
            content: {
              en: 'By submitting your configuration, you consent to the collection, use, and sharing of your personal information with authorized dealers to facilitate your request, in accordance with our [Privacy Statement].',
              ar: 'من خلال إرسال التكوين الخاص بك، فإنك توافق على جمع معلوماتك الشخصية واستخدامها ومشاركتها مع التجار المعتمدين لتسهيل طلبك، وفقًا لـ [بيان الخصوصية] الخاص بنا.',
            },
          },
          item7: {
            title: {
              en: 'System Limitations',
              ar: 'قيود النظام',
            },
            content: {
              en: 'While we aim to provide accurate and current information, the configurator may not reflect all available features or up-to-date pricing. Errors or omissions may occur.',
              ar: 'مع أننا نهدف إلى توفير معلومات دقيقة وحديثة، إلا أن مُكوِّن الإعدادات قد لا يعكس جميع الميزات المتاحة أو الأسعار المُحدَّثة. قد تحدث أخطاء أو سهو.',
            },
          },
        },
      },
    },
    common: {
      exit: {
        en: 'Exit',
        ar: 'خروج',
      },
      close: {
        en: 'Close',
        ar: 'إغلاق',
      },
      cancel: {
        en: 'Cancel',
        ar: 'إلغاء',
      },
      back: {
        en: 'Back',
        ar: 'خلف',
      },
      next: {
        en: 'Next',
        ar: 'التالي',
      },
      done: {
        en: 'Done',
        ar: 'تم',
      },
      exterior: {
        en: 'Exterior',
        ar: 'الخارجة',
      },
      interior: {
        en: 'Interior',
        ar: 'الداخلية',
      },
      enterFullscreen: {
        en: 'Enter Fullscreen',
        ar: 'ملء الشاشة',
      },
      exitFullscreen: {
        en: 'Exit Fullscreen',
        ar: 'إلغاء ملء الشاشة',
      },
      selectTrim: {
        en: 'Select Trim',
        ar: 'اختر الفئة',
      },
      viewConfiguration: {
        en: 'View Configuration',
        ar: 'عرض التكوين',
      },
      togglePanelTooltip: {
        en: 'Select Trim and Configuration',
        ar: 'اختر الفئة والتكوين',
      },
      nextStepTooltip: {
        en: 'Click to proceed to the next step',
        ar: 'انقر للانتقال إلى الخطوة التالية',
      },
      heading: {
        en: 'Standard Features',
        ar: 'المميزات  القياسية',
      },
      description: {
        en: 'Chevrolet Arabia',
        ar: 'شيفروليه العربية',
      },
      goBack: { en: 'Go Back', ar: 'العودة' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'عرض مميز' },
      configurator: { en: 'Configurator', ar: 'المُكوِّن' },
      buildYourRide: { en: 'Build Your Ride', ar: 'ابنِ سيارتك' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
      saveYourBuild: { en: 'Save Your Build', ar: 'احفظ تكوينك' },
      startingPrice: { en: 'Starting Price', ar: 'السعر الابتدائي' },
      msrp: { en: 'MSRP', ar: 'سعر التجزئة المقترح' },
      proceed: { en: 'Proceed', ar: 'متابعة' },
    },
    tabs: {
      config: { en: 'Config', ar: 'الإعداد' },
      model: { en: 'Model', ar: 'الموديل' },
      exterior: { en: 'Exterior', ar: 'الخارجي' },
      interior: { en: 'Interior', ar: 'الداخلي' },
      options: { en: 'Options', ar: 'الخيارات' },
      summary: { en: 'Summary', ar: 'الملخص' },
    },
    cart: {
      title: {
        en: 'Your Order',
        ar: 'طلبك',
      },
      disclaimer: {
        en: 'Accessory prices and availability will be confirmed at the showroom.',
        ar: 'سيتم تأكيد أسعار الملحقات وتوافرها في صالة العرض.',
      },
      exteriorLabel: {
        en: 'Exterior:',
        ar: ':خارجي',
      },
      interiorLabel: {
        en: 'Interior:',
        ar: ':داخلي',
      },
      addedAccessories: {
        en: 'Added Accessories',
        ar: 'الإكسسوارات المضافة',
      },
      priceEstimate: {
        en: 'Price Estimate',
        ar: 'السعر التقديري',
      },
      priceBreakdown: {
        en: 'Price Breakdown',
        ar: 'تفاصيل السعر',
      },
      reserveNow: {
        en: 'Proceed',
        ar: 'متابعة',
      },
      removeAccessory: {
        en: 'Remove accessory',
        ar: 'إزالة الإكسسوار',
      },
      removeItem: { en: 'Remove', ar: 'إزالة' },
      packageCategory: { en: 'Package', ar: 'حزمة' },
    },
    saveDialog: {
      optionsTitle: {
        en: 'How would you like to proceed?',
        ar: 'كيف تريد المتابعة؟',
      },
      optionWhatsapp: { en: 'Save to WhatsApp', ar: 'حفظ على واتساب' },
      optionEmail: { en: 'Save to Email', ar: 'حفظ على البريد الإلكتروني' },
      optionTestDrive: {
        en: 'Book a Test Drive',
        ar: 'احجز اختبار قيادة',
      },
      optionCallback: {
        en: 'Request a call Back',
        ar: 'طلب معاودة الاتصال',
      },
      labelFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      labelLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      labelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      labelMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      labelPrefix: {
        en: 'Prefix',
        ar: 'البادئة',
      },
      labelModel: {
        en: 'Model',
        ar: 'نموذج',
      },
      labelDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      labelComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      labelTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      labelTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderTestDriveDate: {
        en: 'Preferred date*',
        ar: 'التاريخ المفضل*',
      },
      placeholderTestDriveTime: {
        en: 'Preferred time*',
        ar: 'الوقت المفضل*',
      },
      placeholderFirstName: {
        en: 'First Name*',
        ar: 'الاسم الأول',
      },
      placeholderLastName: {
        en: 'Last Name*',
        ar: 'اسم العائلة',
      },
      placeholderEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      placeholderMobileNumber: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      placeholderComments: {
        en: 'Comments',
        ar: 'تعليقات',
      },
      placeholderSelectDealership: {
        en: 'Preferred Dealership*',
        ar: 'الوكيل المفضّل',
      },
      privacyTitle: {
        en: 'Privacy statement & legal disclaimer',
        ar: 'بيان الخصوصية وإخلاء المسؤولية القانونية',
      },
      privacyText: {
        en: 'I acknowledge and understand that my information will be shared with General Motors, its affiliates, dealer network or other parties are required by law for compliance, safety campaigns, government inquiries, or similar legal process. My information may also be shared for product research and development purposes, and to manage customer relationships to provide support. GM’s Privacy Statement addresses how GM handles the personal information shared with us.',
        ar: 'أقر وأتفهم بأنة سيتم مشاركة معلوماتي مع شركة جنرال موتورز، والشركات التابعة لها، وشبكة الوكلاء أو الأطراف الأخرى بموجب القانون للامتثال وحملات السلامة واستفسارات الحكومية وأي إجراء قانوني مماثل. وقد تتم مشاركة معلوماتي أيضًا لأغراض البحث وتطوير المنتجات ، وإدارة علاقة العملاء لتوفير الدعم اللازم. يوضح بيان خصوصية جنرال موتورز على موقعنا كيفية تعامل جنرال موتورز مع المعلومات الشخصية التي تمت مشاركتها معنا.',
      },
      privacyReadMore: {
        en: 'Read more',
        ar: 'اقرأ المزيد',
      },
      privacyMarketingConsent: {
        en: 'I wish to receive any marketing information or have my information shared with third parties for purposes of providing me with marketing information.',
        ar: 'أرغب في تلقي معلومات تسويقية أو مشاركة معلوماتي مع جهات خارجية لغاية تزويدي بمعلومات تسويقية',
      },
      buttonSend: {
        en: 'Send Configuration',
        ar: 'إرسال التكوين',
      },
      buttonSending: {
        en: 'Submitting...',
        ar: 'تقديم...',
      },
      buttonSent: {
        en: 'Sent!',
        ar: 'مرسل!',
      },
      buttonRetry: {
        en: 'Retry',
        ar: 'إعادة المحاولة',
      },
      buttonClose: {
        en: 'Okay, Got it',
        ar: 'حسنًا، فهمت',
      },
      successTitle: {
        en: 'Saved',
        ar: 'محفوظ',
      },
      successLabelModel: {
        en: 'Model Name',
        ar: 'اسم النموذج',
      },
      successLabelVariant: {
        en: 'Variant Name',
        ar: 'اسم البديل',
      },
      successLabelName: {
        en: 'Name',
        ar: 'اسم',
      },
      successLabelEmail: {
        en: 'Email*',
        ar: 'البريد الالكتروني',
      },
      successLabelMobile: {
        en: 'Mobile Number*',
        ar: 'رقم الجوال',
      },
      qrTitle: {
        en: 'Your Unique Configuration QR',
        ar: 'رمز الاستجابة السريعة لتكوينك',
      },
      qrDescription: {
        en: 'Show this QR code to your nearest dealer to access your saved configuration.',
        ar: 'أظهر رمز الاستجابة السريعة هذا لتاجر التجزئة الأقرب للوصول إلى التكوين المحفوظ.',
      },
      qrNoteLabel: {
        en: 'Note:',
        ar: 'ملاحظة:',
      },
      qrNote: {
        en: 'Only authorized dealers can view this configuration.',
        ar: 'يمكن للوكلاء المعتمدين فقط عرض هذا التكوين.',
      },
      qrDownload: {
        en: 'Download QR',
        ar: 'تحميل QR',
      },
      qrSave: {
        en: 'Save QR',
        ar: 'حفظ QR',
      },
      errorTitle: {
        en: 'Submission Failed',
        ar: 'فشل التقديم',
      },
      errorMessage: {
        en: 'We encountered an error, please try again.',
        ar: 'واجهنا خطأ، يرجى المحاولة مرة أخرى.',
      },
    },
    featureGroups: {
      exteriorDesign: {
        en: 'Exterior Design & Styling',
        ar: 'التصميم الخارجي والتصميم',
      },
      interiorComfort: {
        en: 'Interior Comfort & Features',
        ar: 'الراحة والميزات الداخلية',
      },
      technologyConvenience: {
        en: 'Technology & Convenience',
        ar: 'التكنولوجيا والراحة',
      },
      safetyAssistance: {
        en: 'Safety & Driver Assistance',
        ar: 'السلامة ومساعدة السائق',
      },
    },
    sidePanel: {
      menu: { en: 'Main Menu', ar: 'القائمة الرئيسية' },
      search: { en: 'Search', ar: 'بحث' },
      settings: { en: 'Settings', ar: 'الإعدادات' },
      profile: { en: 'Profile', ar: 'الملف الشخصي' },
      notifications: { en: 'Notifications', ar: 'الإشعارات' },
      help: { en: 'Help & Support', ar: 'المساعدة والدعم' },
      filter: { en: 'Filter Options', ar: 'خيارات التصفية' },
      favorites: { en: 'Favorites', ar: 'المفضلة' },
      history: { en: 'History', ar: 'التاريخ' },
      tools: { en: 'Tools', ar: 'الأدوات' },
      variants: { en: 'Variants', ar: 'الأنواع' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'ألوان المقصورة' },
      features: { en: 'Features', ar: 'الميزات' },
      step: { en: 'Step', ar: 'خطوة' },
      openPanel: { en: 'Open Panel', ar: 'فتح اللوحة' },
      closePanel: { en: 'Close Panel', ar: 'إغلاق اللوحة' },
    },
    landscape: {
      message: {
        en: 'Please rotate your device to portrait mode',
        ar: 'يرجى تدوير جهازك إلى الوضع الرأسي',
      },
      subtitle: {
        en: 'For the best experience, please turn your device upright.',
        ar: 'للحصول على أفضل تجربة، يرجى إمساك جهازك بشكل رأسي.',
      },
    },
    cameraViewToggle: {
      ext: { en: 'Ext', ar: 'خارجي' },
      int: { en: 'Int', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
    },
    bottomDrawer: {
      variants: { en: 'Variants', ar: 'الأنواع' },
      interactions: { en: 'Interactions', ar: 'التفاعلات' },
      accessories: { en: 'Options & Accessories', ar: 'الإكسسوارات' },
      features: { en: 'Features', ar: 'الميزات' },
      highlights: { en: 'Highlights', ar: 'الميزات البارزة' },
      moreOptions: { en: 'More Options', ar: 'المزيد من الخيارات' },
      colorSelector: { en: 'Color Selector', ar: 'محدد اللون' },
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      wheels: { en: 'Wheels', ar: 'العجلات' },
      roofs: { en: 'Roofs', ar: 'الأسقف' },
      performance: { en: 'Performance', ar: 'الأداء' },
      packages: { en: 'Packages', ar: 'الحزم' },
      chargingOptions: { en: 'Charging Options', ar: 'خيارات الشحن' },
      safetyServices: { en: 'Safety & Services', ar: 'السلامة والخدمات' },
      expand: { en: 'Expand', ar: 'توسيع' },
      minimize: { en: 'Minimize', ar: 'تصغير' },
      freeRoamView: { en: 'Free Roam View', ar: 'عرض التجوال الحر' },
      featureView: { en: 'Featured View', ar: 'العرض المميز' },
    },
    qr: {
      scan: { en: 'Scan QR', ar: 'امسح رمز الاستجابة السريعة' },
      scanWithPhone: {
        en: 'Scan with your phone to view in your Space',
        ar: 'امسح باستخدام هاتفك لعرضه في مساحتك',
      },
    },
    share: {
      title: { en: 'Share Configuration', ar: 'مشاركة التكوين' },
      description: {
        en: 'Share your vehicle configuration with others',
        ar: 'شارك تكوين سيارتك مع الآخرين',
      },
      twitterTitle: {
        en: 'Check out my Chevrolet Traverse configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      whatsappTitle: {
        en: 'Check out my Chevrolet Traverse configuration!',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس!',
      },
      linkedinTitle: {
        en: 'My Chevrolet Traverse Configuration',
        ar: 'تكويني لسيارة شيفروليه ترافيرس',
      },
      linkedinSummary: {
        en: 'Check out my custom Chevrolet Traverse configuration!',
        ar: 'شاهد تكويني المخصص لسيارة شيفروليه ترافيرس!',
      },
      emailSubject: {
        en: 'Check out my Chevrolet Traverse Configuration',
        ar: 'شاهد تكوين سيارتي شيفروليه ترافيرس',
      },
      emailBody: {
        en: 'I configured my dream Chevrolet Traverse. Take a look:',
        ar: 'قمت بتكوين سيارة شيفروليه ترافيرس المثالية. ألق نظرة:',
      },
      linkLabel: { en: 'Or copy link:', ar: 'أو انسخ الرابط:' },
      copied: {
        en: 'Copied',
        ar: 'تم نسخ الرابط إلى الحافظة!',
      },
      copy: { en: 'Copy', ar: 'نسخ' },
      saveAndShare: { en: 'Save & Share', ar: 'حفظ ومشاركة' },
      copyLink: { en: 'Copy link', ar: 'نسخ الرابط' },
      facebook: { en: 'Facebook', ar: 'Facebook' },
      twitter: { en: 'Twitter', ar: 'Twitter' },
      gmail: { en: 'Gmail', ar: 'Gmail' },
      whatsapp: { en: 'Whatsapp', ar: 'Whatsapp' },
    },
    topBar: {
      matchingVehiclesFound: {
        en: 'Matching Vehicles Found',
        ar: 'تم العثور على المركبات المطابقة',
      },
    },
    bottomBar: {
      priceLabel: { en: 'ex showroom price', ar: 'سعر صالة العرض' },
      leads: { en: 'Save', ar: 'حفظ' },
      findLocalMatches: {
        en: 'Find Local Matches',
        ar: 'اعثر على التطابقات المحلية',
      },
      save: { en: 'Save', ar: 'حفظ' },
      item: { en: 'Item', ar: 'العنصر' },
      items: { en: 'Items', ar: 'عناصر' },
      totalToPay: { en: 'Total to pay', ar: 'الإجمالي للدفع' },
      bookTestDrive: { en: 'Save', ar: 'حفظ' },
      requestQuote: { en: 'Request a Quote', ar: 'طلب عرض سعر' },
      downloadBrochure: { en: 'Download Brochure', ar: 'تحميل الكتيب' },
    },
    priceSummary: {
      title: { en: 'Price Summary', ar: 'ملخص السعر' },
      enterLocation: { en: 'Enter Location', ar: 'أدخل الموقع' },
      selectCountry: { en: 'Location', ar: 'الموقع' },
      selectCity: { en: 'Location', ar: 'الموقع' },
      note: {
        en: 'Note : Prices might vary by state and all prices listed are on road price, T&C apply.',
        ar: 'ملاحظة: قد تختلف الأسعار حسب الولاية وجميع الأسعار المذكورة هي الأسعار على الطريق، تطبق الشروط والأحكام.',
      },
      breakdown: { en: 'Price Breakdown', ar: 'تفصيل السعر' },
      color: { en: 'Color', ar: 'اللون' },
      variant: { en: 'Variant', ar: 'الفئة' },
      included: { en: 'Included', ar: 'مشمول' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    featureDialog: {
      playPause: { en: 'Play-Pause', ar: 'تشغيل-إيقاف' },
    },
    selectDealership: {
      title: { en: 'Select a Dealer', ar: 'اختر وكيلاً' },
      subtitle: {
        en: 'Please select a dealer to find local matches similar to your configuration',
        ar: 'يرجى اختيار وكيل للعثور على سيارات مطابقة لتكوينك في منطقتك',
      },
      city: { en: 'UAE', ar: 'الإمارات' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      dealersInCity: { en: 'Dealers in {{city}}', ar: 'وكلاء في {{city}}' },
      availableInLocations: {
        en: 'Available in {{count}} locations of {{city}}',
        ar: 'متوفرة في {{count}} مواقع في {{city}}',
      },
      proceed: {
        en: 'Select Dealer and Proceed to Configurator',
        ar: 'اختر الوكيل وتابع إلى المتكون',
      },
    },
    countryNames: {
      UAE: { en: 'UAE', ar: 'الإمارات' },
      KSA: { en: 'KSA', ar: 'السعودية' },
      KW: { en: 'Kuwait', ar: 'الكويت' },
      default: { en: 'UAE', ar: 'الإمارات' },
    },
    localMatches: {
      exterior: { en: 'Exterior', ar: 'خارجي' },
      interior: { en: 'Interior', ar: 'داخلي' },
      dualTone: { en: 'Dual-tone', ar: 'لون مزدوج' },
      yourConfiguration: { en: 'Current Configuration', ar: 'التكوين الحالي' },
      viewConfiguration: {
        en: 'View your configuration details',
        ar: 'عرض تفاصيل تكوينك',
      },
      yourBuildDetails: { en: 'Your Build Details', ar: 'تفاصيل بنائك' },
      options: { en: 'Options', ar: 'الخيارات' },
      save: { en: 'Save', ar: 'حفظ' },
      dealersIn: { en: 'Dealers in', ar: 'وكلاء في' },
      changeDealer: { en: 'Change Dealer', ar: 'تغيير الوكيل' },
      carsAvailable: {
        en: 'Cars Available at the Selected Dealership',
        ar: 'السيارات المتوفرة لدى الوكيل المختار',
      },
      matchesFound: { en: 'Matches found', ar: 'تطابق تم العثور عليه' },
      startingFrom: { en: 'Starting from', ar: 'يبدأ من' },
      getQuote: { en: 'Proceed', ar: 'احصل على عرض سعر' },
      viewDifferences: { en: 'View Differences', ar: 'عرض الاختلافات' },
      match: { en: 'Match', ar: 'تطابق' },
      closestMatchFound: {
        en: 'Closest Match Found',
        ar: 'أقرب تطابق تم العثور عليه',
      },
      buildDetails: { en: 'Build Details', ar: 'تفاصيل التكوين' },
      trim: { en: 'Trim', ar: 'الفئة' },
      matchScoreTooltip: {
        en: 'This score indicates how closely this vehicle matches your configuration.',
        ar: 'يشير هذا النتيجة إلى مدى تطابق هذه السيارة مع تكوينك.',
      },
      exactMatch: { en: 'Exact Match', ar: 'تطابق تام' },
      similarMatch: { en: 'Close Match', ar: 'تطابق قريب' },
      availableNow: { en: 'Available Now', ar: 'متوفر الآن' },
      whatYouConfigured: { en: 'What you configured', ar: 'ما قمت بتكوينه' },
      matchingInStock: { en: 'Matching vehicle available in stock', ar: 'مركبة مطابقة متوفرة في المخزون' },
      noMatches: {
        en: 'No matching vehicles found in your area.',
        ar: 'لم يتم العثور على سيارات مطابقة في منطقتك.',
      },
      closeMatchDetails: { en: 'Close Match Details', ar: 'تفاصيل التطابق القريب' },
      matchesYourSelection: { en: 'Matches your selection', ar: 'يطابق اختيارك' },
      sameAsWhatYouSelected: { en: 'Same as what you selected', ar: 'نفس ما اخترته' },
      replacesYourSelection: { en: 'Replaces your selection', ar: 'يستبدل اختيارك' },
      selectionNotIncluded: { en: 'Selection not included', ar: 'الاختيار غير مشمول' },
      includedInThisVehicle: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      additionalFeatureAlreadyIncluded: { en: 'Additional feature already included', ar: 'ميزة إضافية مشمولة بالفعل' },
      stockDisclaimer: { en: 'Available stock may include extra features not selected in your original configuration.', ar: 'قد تشمل المركبات المتاحة ميزات إضافية لم تختارها في تكوينك الأصلي.' },
      additionalInstalledOptionsLegend: { en: 'Included in this vehicle', ar: 'مشمول في هذه المركبة' },
      vehicleDetails: { en: 'Vehicle Details', ar: 'تفاصيل المركبة' },
      installedOptionsTitle: { en: 'Comes with vehicle', ar: 'يأتي مع المركبة' },
      additionalInstalledOptionsSubtitle: { en: 'Additional Installed Options:', ar: 'الخيارات المركبة الإضافية:' },
      specTrim: { en: 'Trim', ar: 'الفئة' },
      specExteriorColor: { en: 'Exterior Color', ar: 'اللون الخارجي' },
      specInteriorColor: { en: 'Interior Color', ar: 'اللون الداخلي' },
      specWheels: { en: 'Wheels', ar: 'العجلات' },
      specPackage: { en: 'Package', ar: 'الحزمة' },
      title: {
        en: 'Configurations Found',
        ar: 'تكوينات تم العثور عليها',
      },
      message: {
        en: 'We Found a few local matches similar to your current configuration. Click to view Matches',
        ar: 'وجدنا بعض التطابقات المحلية المشابهة لتكوينك الحالي. انقر لعرض التطابقات',
      },
    },
    trim: {
      selection: { en: 'Trim Selection', ar: 'اختيار الفئة' },
    },
    paymentPage: {
      exteriorColor: { en: 'Exterior Color', ar: 'لون الهيكل' },
      interiorColor: { en: 'Interior Color', ar: 'لون المقصورة' },
      accessory: { en: 'Accessory', ar: 'ملحق' },
      closePayment: { en: 'Close', ar: 'إغلاق' },
    },
    packageModal: {
      includes: { en: 'Includes:', ar: 'يشمل:' },
      of: { en: 'of', ar: 'من' },
      totalPriceChange: {
        en: 'Total Price Change:',
        ar: 'إجمالي تغيير السعر:',
      },
      item: { en: 'Item', ar: 'غرض' },
      items: { en: 'Items', ar: 'أغراض' },
      addPackage: { en: 'Add Package', ar: 'إضافة الحزمة' },
      noThanks: { en: 'No Thanks', ar: 'لا شكراً' },
      alreadyApplied: { en: 'Already Applied', ar: 'مُطبّق بالفعل' },
    },
    changesRequired: { en: 'Changes Required', ar: 'التغييرات المطلوبة' },
    changesRequiredDescription: {
      en: 'The package you selected conflicts with your current selections. Please review the changes below.',
      ar: 'الحزمة التي اخترتها تتعارض مع اختياراتك الحالية. يرجى مراجعة التغييرات أدناه.',
    },
    itemsThatWillBeRemoved: {
      en: 'Items that will be removed',
      ar: 'العناصر التي سيتم إزالتها',
    },
    itemsThatWillBeAdded: {
      en: 'Items that will be added',
      ar: 'العناصر التي سيتم إضافتها',
    },
    applyChanges: { en: 'Apply Changes', ar: 'تطبيق التغييرات' },
    previewFeature: { en: 'Preview Feature', ar: 'معاينة الميزة' },
    exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    buildAndPrice: { en: 'Build & Price', ar: 'البناء والسعر' },
    features: {
      preview: { en: 'Preview Feature', ar: 'معاينة المميزات' },
      playAll: { en: 'PLAY ALL', ar: 'تشغيل الكل' },
      title: { en: 'Features', ar: 'المميزات' },
    },
    header: {
      localMatches: { en: 'Local\nMatches', ar: 'التطابقات\nالمحلية' },
      matchingVehicles: { en: 'Matching vehicles', ar: 'مركبات مطابقة' },
      configurationsFound: { en: 'Configurations Found', ar: 'تكوينات تم العثور عليها' },
    },
    configPanel: {
      playAll: { en: 'Play All', ar: 'تشغيل الكل' },
      pause: { en: 'Pause', ar: 'إيقاف مؤقت' },
      exteriorColors: { en: 'Exterior Colors', ar: 'ألوان الهيكل الخارجي' },
      interiorColors: { en: 'Interior Colors', ar: 'ألوان الداخلية' },
      roofOptions: { en: 'Roof Options', ar: 'خيارات السقف' },
      interiorOptions: { en: 'Interior Options', ar: 'خيارات الداخلية' },
      exteriorAccessories: { en: 'Exterior Accessories', ar: 'إكسسوارات خارجية' },
      noAdditionalOptions: { en: 'No additional options available for the selected trim.', ar: 'لا توجد خيارات إضافية متاحة للفئة المختارة.' },
      noFilters: { en: 'No filters available.', ar: 'لا توجد فلاتر متاحة.' },
      noVariantsMatch: { en: 'No trims match the selected filters.', ar: 'لا توجد فئات تطابق الفلاتر المختارة.' },
      configurationBreakdown: { en: 'Configuration Breakdown', ar: 'تفاصيل التكوين' },
      yourConfiguration: { en: 'Your Configuration', ar: 'تكوينك' },
      trim: { en: 'Trim', ar: 'الفئة' },
      total: { en: 'Total', ar: 'الإجمالي' },
    },
    conflictDialog: {
      description: { en: 'In order to apply this, the following item(s) will need to be removed due to compatibility constraints. Please review the item(s) below and proceed with your change.', ar: 'لتطبيق هذا، يجب إزالة العناصر التالية بسبب قيود التوافق. يرجى مراجعة العناصر أدناه والمتابعة بتغييرك.' },
    },
    viewport: {
      changeBackground: { en: 'Change Background', ar: 'تغيير الخلفية' },
      selectLanguage: { en: 'Switch Language (عربي)', ar: 'تبديل اللغة (EN)' },
      menu: { en: 'Menu', ar: 'القائمة' },
      exploreFeatures: { en: 'Explore Features', ar: 'استكشف الميزات' },
    },
  },
  dealers: {
    UAE: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - منطقة القوز الصناعية 3، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد، صالة العرض، الخبيصي، ديرة، دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
    KSA: [
      {
        id: 0,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307261',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Aziziyah Madina Road, Jeddah',
          ar: 'الجميح للسيارات - العزيزية طريق المدينة، جدة',
        },
      },
      {
        id: 1,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164034',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Hail City King Abdullah Road. Hail',
          ar: 'الجميح للسيارات - طريق الملك عبدالله، مدينة حائل، حائل',
        },
      },
      {
        id: 2,
        country: 'KSA',
        value: 'KSA',
        dealerId: '164035',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Alshlhoub King Abdullah Road, Al Jouf',
          ar: 'الجميح للسيارات - الشلهوب، طريق الملك عبدالله، الجوف',
        },
      },
      {
        id: 3,
        country: 'KSA',
        value: 'KSA',
        dealerId: '307271',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Qassim King Abdulaziz Road, Buraidah',
          ar: 'الجميح للسيارات - القصيم طريق الملك عبد العزيز، بريدة',
        },
      },
      {
        id: 4,
        country: 'KSA',
        value: 'KSA',
        dealerId: '260921',
        dealerName: 'Aljomaih Auto',
        label: {
          en: 'Aljomaih Auto - Al Jededah Road, Arar',
          ar: 'الجميح للسيارات - طريق الجديدة، عرعر',
        },
      },
    ],
    KW: [
      {
        id: 0,
        country: 'KW',
        value: 'KW',
        dealerId: '122620',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Al Rai 4th Ring Road, Al Rai',
          ar: 'الأمانة للسيارات - الطريق الدائري الرابع، الري',
        },
      },
      {
        id: 1,
        country: 'KW',
        value: 'KW',
        dealerId: '174979',
        dealerName: 'Alamana Auto',
        label: {
          en: 'Alamana Auto - Fahaheel Industrial Area Dabbous Street, Fahaheel',
          ar: 'الأمانة للسيارات - منطقة الفحيحيل الصناعية، شارع الدبوس، الفحيحيل',
        },
      },
    ],
    BHD: [
      {
        id: 0,
        country: 'BHD',
        value: 'BHD',
        dealerId: '122215',
        dealerName: 'National Motor Company W.L.L',
        label: {
          en: 'National Motor Company W.L.L, Al Qarya 475 Shk. Jaber Al Subah Hwy',
          ar: 'الشركة الوطنية للسيارات ذ.م.م، القرية 475، طريق الشيخ جابر الأحمد الصباح',
        },
      },
    ],
    IQD: [
      {
        id: 0,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295876',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Al-Karada Sadoun Street',
          ar: 'شركة ماك الدولية ش.م.ح، شارع الكرادة سعدون',
        },
      },
      {
        id: 1,
        country: 'IQD',
        value: 'IQD',
        dealerId: '295879',
        dealerName: 'MAC International FZE',
        label: {
          en: 'MAC International FZE, Kurdistan District 100 m Street',
          ar: 'شركة ماك الدولية ش.م.ح، منطقة كردستان، شارع 100 متر',
        },
      },
    ],
    LBP: [
      {
        id: 0,
        country: 'LBP',
        value: 'LBP',
        dealerId: '122226',
        dealerName: 'IMPEX',
        label: {
          en: 'IMPEX -  Badaro, Near the National Museum , Beirut',
          ar: 'إمبكس - بدارو، بالقرب من المتحف الوطني، بيروت',
        },
      },
    ],
    QAR: [
      {
        id: 0,
        country: 'QAR',
        value: 'QAR',
        dealerId: '274766',
        dealerName: 'Jaidah',
        label: {
          en: 'Jaidah Square Showroom - Al Matar Street Umm Ghuwallina, Doha',
          ar: 'صالة عرض جايدة سكوير - شارع المطار، أم غويلينة، الدوحة',
        },
      },
    ],
    OMR: [
      {
        id: 0,
        country: 'OMR',
        value: 'OMR',
        dealerId: '122219',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Ghalla Al Maardh Street',
          ar: 'مؤسسة عمان التجارية ش.م.م، شارع غلا المعرض',
        },
      },
      {
        id: 1,
        country: 'OMR',
        value: 'OMR',
        dealerId: '234217',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Al Farq Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع الفرق',
        },
      },
      {
        id: 2,
        country: 'OMR',
        value: 'OMR',
        dealerId: '260896',
        dealerName: 'Oman Trading Establisment',
        label: {
          en: 'Oman Trading Establisment LLC, Hail Al Shumaliah Street',
          ar: 'مؤسسة عمان التجارية ذ.م.م، شارع حيل الشمالية',
        },
      },
    ],
    JOD: [
      {
        id: 0,
        country: 'JOD',
        value: 'JOD',
        dealerId: '189129',
        dealerName: 'Leading Vehicles',
        label: {
          en: 'The Leading Vehicles Co. Ltd, Al Rawabi',
          ar: 'شركة المركبات الرائدة المحدودة، الروابي',
        },
      },
    ],
    default: [
      {
        id: 0,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122255',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Quoz 3 Industrial Area Sheikh Zayed Road, Dubai',
          ar: 'الكندي للسيارات - منطقة القوز الصناعية 3، طريق الشيخ زايد، دبي',
        },
      },
      {
        id: 1,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256427',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Double Cola Road, Sharjah',
          ar: 'الكندي للسيارات - طريق دبل كولا، الشارقة',
        },
      },
      {
        id: 2,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256428',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Madhab Mohammad Bin Mattar Street, Fujairah',
          ar: 'الغاندي للسيارات - شارع مذهب محمد بن مطر، الفجيرة',
        },
      },
      {
        id: 3,
        country: 'UAE',
        value: 'UAE',
        dealerId: '256429',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Nadiyah Sheikh Mohammed Bin Salem Road, Ras Al Khaimah',
          ar: 'الكندي للسيارات - الندية، طريق الشيخ محمد بن سالم، رأس الخيمة',
        },
      },
      {
        id: 4,
        country: 'UAE',
        value: 'UAE',
        dealerId: '301597',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi Auto - Al Itihad Road - showroom , Al Khabaisi, Deira, Dubai',
          ar: 'الكندي للسيارات - طريق الاتحاد، صالة العرض، الخبيصي، ديرة، دبي',
        },
      },
      {
        id: 8,
        country: 'UAE',
        value: '',
        dealerId: '334821',
        dealerName: 'Al Ghandi Auto',
        label: {
          en: 'Al Ghandi - Mirdiff City Center',
          ar: 'الغاندي - مردف سيتي سنتر',
        },
      },
      {
        id: 5,
        country: 'UAE',
        value: 'UAE',
        dealerId: '323482',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Madinat Zayed, Al Dhafra',
          ar: 'بن حمودة للسيارات - مدينة زايد، الظفرة',
        },
      },
      {
        id: 6,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122552',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Al Ain',
          ar: 'بن حمودة للسيارات - العين',
        },
      },
      {
        id: 7,
        country: 'UAE',
        value: 'UAE',
        dealerId: '122233',
        dealerName: 'Bin Hamoodah Auto',
        label: {
          en: 'Bin Hamoodah Auto - Abu Dhabi Main',
          ar: 'بن حمودة للسيارات - أبوظبي الرئيسية',
        },
      },
    ],
  },
  featureFlags: {
    showPriceInPresentation: false,
    showLogo: true,
    showSpecDetailsButton: true,
    useChevroletFont: true,
    showLocalMatches: true,
    showDisclaimerInPresentation: false,
    // Per-item pricing (card / MODEL / SUMMARY price lines, colours in the total)
    // is opt-in; this project keeps the header-only pricing it has always had.
    showPrices: true, // per-item prices on cards + summary breakdown, like Corvette
    showDisplayCarPrice: true,
    showCart: false
  },
  config: {
    // Display currency + USD conversion factor per region, keyed by the
    // `country` URL parameter (local = usd * fx). Prices were re-derived
    // from the AED figure: usd = AED / 3.68, then usd * fx per country.
    regions: {
      KSA: { label: 'KSA', currency: 'SAR', fx: 3.75 },
      UAE: { label: 'UAE', currency: 'AED', fx: 3.68 },
      OMR: { label: 'Oman', currency: 'OMR', fx: 0.39 },
      BHD: { label: 'Bahrain', currency: 'BHD', fx: 0.38 },
      QAR: { label: 'Qatar', currency: 'QAR', fx: 3.64 },
      JOD: { label: 'Jordan', currency: 'JOD', fx: 0.71 },
      KW: { label: 'Kuwait', currency: 'KWD', fx: 0.31 },
      LBP: { label: 'Lebanon', currency: 'USD', fx: 1 }, // GM quotes Lebanon in USD
      IQD: { label: 'Iraq', currency: 'USD', fx: 1 }, // priced in USD, like Lebanon
      default: { label: 'UAE', currency: 'AED', fx: 3.68 },
    },
    // Lead-side model name — 1.0's `config.model`, which is a lead label
    // distinct from 2.0's `config.model` (the models[] key).
    leadModel: 'tahoe',
    // Dial code (prefixed onto mobile_number) and Salesforce lead country
    // code (metadome_country_code), keyed by the `country` URL parameter.
    countryCodes: {
      UAE: {
        code: '+971',
        leadCode: 'AE',
      },
      KSA: {
        code: '+966',
        leadCode: 'SA',
      },
      KW: {
        code: '+965',
        leadCode: 'KW',
      },
      BHD: {
        code: '+973',
        leadCode: 'BH',
      },
      IQD: {
        code: '+964',
        leadCode: 'IQ',
      },
      LBP: {
        code: '+961',
        leadCode: 'LB',
      },
      OMR: {
        code: '+968',
        leadCode: 'OM',
      },
      QAR: {
        code: '+974',
        leadCode: 'QA',
      },
      JOD: {
        code: '+962',
        leadCode: 'JO',
      },
      default: {
        code: '+971',
        leadCode: 'AE',
      },
    },
    model: 'Tahoe',
    modelName: 'Tahoe',
    pageTitle: 'Tahoe Configurator',
    client: 'GM',
    brand: 'chevrolet',
    modelId: 'CHTAHMCM_2025',
    configurationId: 'l25zrwex1u93',
    externalAnalytics: {
      vehicleType: 'suv',
      brand: 'chevrolet',
      provider: 'adobe',
      modelName: 'tahoe'
    },
    presentation: {
      video: 'videos/2365/presentation_video_1.webm',
      loadingImage: 'images/2365/sliderImage.webp',
      transitionImage: 'images/2365/transition_logo.png',
      headerLogoKeepColor: true,
      keyHighlights: [
        {
          index: 0,
          show: true,
          title: {
            en: '',
            ar: ''
          },
          value: {
            en: '355/420 HP',
            ar: '355/420 حصان'
          },
          unit: ''
        },
        {
          index: 1,
          show: true,
          title: {
            en: 'Torque',
            ar: 'عزم الدوران'
          },
          value: {
            en: '518/624 NM',
            ar: '518/624 نيوتن متر'
          },
          unit: ''
        },
        {
          index: 2,
          show: true,
          title: {
            en: 'V8 Engine',
            ar: 'V8 محرك'
          },
          value: {
            en: '5.3L/6.2L',
            ar: '5.3 لتر/6.2 لتر'
          },
          unit: ''
        }
      ]
    }
  },
  saveFormConfig: {
    fields: [
      {
        id: 'firstName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelFirstName',
        placeholderKey: 'saveDialog.placeholderFirstName',
      },
      {
        id: 'lastName',
        type: 'text',
        required: true,
        labelKey: 'saveDialog.labelLastName',
        placeholderKey: 'saveDialog.placeholderLastName',
      },
      {
        id: 'email',
        type: 'email',
        required: true,
        labelKey: 'saveDialog.labelEmail',
        placeholderKey: 'saveDialog.placeholderEmail',
        visibility: ['email', 'test-drive', 'callback'],
      },
      {
        id: 'phone',
        type: 'phone',
        required: true,
        labelKey: 'saveDialog.labelMobileNumber',
        placeholderKey: 'saveDialog.placeholderMobileNumber',
        visibility: ['whatsapp', 'test-drive', 'callback'],
      },
      {
        id: 'model',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelModel',
        placeholderKey: 'saveDialog.labelModel',
        optionsSource: 'models',
        visibility: ['test-drive', 'callback'],
      },
      {
        id: 'dealer',
        type: 'select',
        required: true,
        labelKey: 'saveDialog.labelDealership',
        placeholderKey: 'saveDialog.placeholderSelectDealership',
        optionsSource: 'dealers',
        visibility: [
          'test-drive',
          'callback',
          'whatsapp',
          'email',
        ],
        // 1.0's save forms show the dealership dropdown but do not gate
        // submission on it; only the booking forms require it.
        requiredFor: [
          'test-drive',
          'callback',
        ],
      },
      {
        id: 'testDriveDate',
        type: 'date',
        required: true,
        labelKey: 'saveDialog.labelTestDriveDate',
        placeholderKey: 'saveDialog.placeholderTestDriveDate',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'testDriveTime',
        type: 'time',
        required: true,
        labelKey: 'saveDialog.labelTestDriveTime',
        placeholderKey: 'saveDialog.placeholderTestDriveTime',
        visibility: [
          'test-drive',
        ],
      },
      {
        id: 'comments',
        type: 'textarea',
        required: false,
        labelKey: 'saveDialog.labelComments',
        placeholderKey: 'saveDialog.placeholderComments',
        visibility: ['test-drive', 'callback'],
      },
    ],
  },
  externalScript: {
    dev: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-bea0833819b4-development.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    staging: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    },
    main: {
      head: {
        links: [
          {
            snippet: 'https://assets.adobedtm.com/ea8c57fea068/636ca1404ad1/launch-85b05c76e071.min.js',
            iframe: true,
            standalone: true,
            async: false
          },
          {
            snippet: 'https://assets.gm.com/responsive-iframe.min.js',
            iframe: true,
            standalone: true
          },
          {
            snippet: 'https://cmp.osano.com/3j6afV3OqT/4661232f-0324-44a1-bbfd-07fe470494c7/osano.js',
            iframe: false,
            standalone: true
          }
        ],
        snippets: [
          {
            snippet: '<!-- Meta Pixel Code -->\n<script>\n!function(f,b,e,v,n,t,s)\n{if(f.fbq)return;n=f.fbq=function(){n.callMethod?\nn.callMethod.apply(n,arguments):n.queue.push(argum\nents)};\nif(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version=\'2.0\';\nn.queue=[];t=b.createElement(e);t.async=!0;\nt.src=v;s=b.getElementsByTagName(e)[0];\ns.parentNode.insertBefore(t,s)}(window,\ndocument,\'script\',\n\'https://connect.facebook.net/en_US/fbevents.js\');\nfbq(\'init\', \'2090290248171811\');\nfbq(\'track\', \'PageView\');\n</script>\n<noscript><img height="1" width="1"\nstyle="display:none"\nsrc="https://www.facebook.com/tr?\nid=2090290248171811&ev=PageView&noscript=1"\n/></noscript>\n<!-- End Meta Pixel Code -->\n        ',
            iframe: true,
            standalone: true
          },
          {
            snippet: '<!-- Google tag (gtag.js) -->\n<script async src="https://www.googletagmanager.com/gtag/js?id=AW-865363499"></script>\n<script>\nwindow.dataLayer = window.dataLayer || [];\nfunction gtag(){dataLayer.push(arguments);}\ngtag(\'js\', new Date());\ngtag(\'config\', \'AW-865363499\');\ngtag(\'config\', \'AW-780743336\');\ngtag(\'config\', \'AW-1050874867\');\ngtag(\'config\', \'AW-1033240730\');\n</script>\n        ',
            iframe: true,
            standalone: true
          }
        ]
      },
      body: {
        snippets: [
          {
            snippet: '<script type="text/javascript">_satellite.pageBottom();</script>',
            iframe: true,
            standalone: true,
            async: false
          }
        ]
      }
    }
  },
  filterOptions: [
    {
      id: 'modelyear',
      key: 'modelyear',
      label: {
        en: 'Model Year',
        ar: 'سنة الطراز'
      },
      disabled: false,
      type: 'chip',
      options: [
        {
          id: '2025',
          label: {
            en: '2025',
            ar: '2025'
          },
          value: '2025',
          checked: false
        },
        {
          id: '2026',
          label: {
            en: '2026',
            ar: '2026'
          },
          value: '2026',
          checked: true
        }
      ]
    }
  ],
  styleConfig: {
    cssVariables: {
      '--default-font-family': 'Chevy Sans',
      '--bold-font-family': 'Chevy Sans Medium',
    },
  },
} as unknown as UIMetadataComplete;
